// Wired Slide Library view — lifted from SlideLibrary.prototype.tsx and
// bound to the real backend through src/api/client.ts.
//
// Layout: a Browse view (consumer), a Manage view (curator/admin only),
// and an Analytics view (admin only). The prototype's per-slide selection
// grid is omitted until the backend exposes a per-slide listing endpoint
// (see backend/src/functions/deckUpload.ts — slides are written by the
// upload worker).

import { useEffect, useMemo, useState } from 'react';
import {
  Search, Plus, Settings, Users, Upload, Send, ThumbsUp, ThumbsDown,
  BarChart3, Clock, AlertCircle, X, Mail, Layers, FileText, Trash2, ChevronDown, ChevronUp,
  Loader2, RefreshCw, Palette, Image as ImageIcon, Check, Bell, Inbox, Lock,
  Tags, GripVertical,
} from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { type AppMode } from '../hooks/useOfficeContext';
import { api, ApiError, MeResponse, Deck, Curator, Slide, Branding, Notification, NotificationLink, NotificationPrefs, GraphGroup, TeamRef, ChannelRef, CategoryTree, CategoryNode, Role, ROLES, NotificationEvent, NotificationTemplate } from '../api/client';
import { renderTemplate as renderTemplatePreview, SAMPLE_VARS } from '../lib/templates';

type View = 'browse' | 'manage' | 'analytics';
type ManageTab = 'drafts' | 'review' | 'slides' | 'curators' | 'branding' | 'categories' | 'notifications';

interface SlideLibraryProps {
  me: MeResponse;
  mode: AppMode;
  host: string;
  branding: Branding | null;
  onBrandingChange: () => void;
}

// "All" pseudo-category id used by Browse for the no-filter state. It is
// not stored in the tree; it's a UI-only sentinel.
const ALL_CATEGORY = '__all__';

// Deterministic gradient based on deck id so cards stay stable across renders
const GRADIENTS = [
  'from-blue-500 to-indigo-600',
  'from-emerald-500 to-teal-600',
  'from-purple-500 to-pink-600',
  'from-orange-500 to-red-600',
  'from-cyan-500 to-blue-600',
  'from-amber-500 to-orange-600',
  'from-violet-500 to-purple-600',
  'from-rose-500 to-pink-600',
  'from-slate-500 to-slate-700',
];
function gradientFor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return GRADIENTS[h % GRADIENTS.length];
}

// Tree → display label. Falls back to the slug when the slug isn't in the
// tree (e.g. category was removed since the deck was created).
function categoryLabel(tree: CategoryTree | null | undefined, slug: string): string {
  return tree?.categories.find(c => c.id === slug)?.name ?? slug;
}
function subCategoryLabel(tree: CategoryTree | null | undefined, catSlug: string, subSlug: string): string {
  const cat = tree?.categories.find(c => c.id === catSlug);
  return cat?.subCategories.find(s => s.id === subSlug)?.name ?? subSlug;
}

function roleLabel(role: Role): string {
  // The approval workflow is deactivated. Contributor + curator collapse
  // to "Designer" for display purposes; both can upload, edit, and
  // publish identically. Admin keeps its dedicated label since admins
  // also control tenant settings. Viewer is a read-only role for tenant
  // members who aren't in the Designers AAD group (and have no per-user
  // override) — they can browse + insert published slides.
  if (role === 'admin') return 'Admin';
  if (role === 'viewer') return 'Viewer';
  return 'Designer';
}
function canReview(role: Role): boolean { return role !== 'contributor' && role !== 'viewer'; }
// Can the user author? (upload, edit, publish, manage own drafts.) Viewers
// can't; everyone else can. UI gates the New Draft button + Manage tab.
function canAuthor(role: Role): boolean { return role !== 'viewer'; }

// Inline status indicator shown on deck rows so contributors can tell at a
// glance whether their work is still editable (draft), waiting on a curator
// (pending_review), or live for everyone (published).
function StatusPill({ status }: { status: Deck['status'] }) {
  const styles: Record<Deck['status'], { label: string; cls: string }> = {
    draft:          { label: 'Draft',     cls: 'bg-slate-100 text-slate-600' },
    pending_review: { label: 'In review', cls: 'bg-blue-100 text-blue-800' },
    published:      { label: 'Published', cls: 'bg-emerald-100 text-emerald-800' },
  };
  const s = styles[status];
  return (
    <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium ${s.cls}`}>{s.label}</span>
  );
}

// Deck cover thumbnail. When the worker has split the deck, we use the
// first slide's PNG; otherwise we fall back to the deterministic gradient
// so unprocessed drafts still get a colour-coded chip.
function DeckCover({ deck, className }: { deck: Deck; className: string }) {
  if (deck.firstSlideThumbnailUrl) {
    return (
      <img
        src={deck.firstSlideThumbnailUrl}
        alt=""
        className={`${className} object-cover bg-slate-100 border border-slate-200`}
        loading="lazy"
      />
    );
  }
  return <div className={`${className} bg-gradient-to-br ${gradientFor(deck.id)}`} />;
}

// Result of a single insert call. `hostUnavailable` means PowerPoint.run
// isn't present (e.g. web mode). `nextTargetId` is the id of the slide we
// just inserted — pass it as the next call's targetSlideId so a sequence of
// inserts stacks in order rather than scattering.
type InsertResult =
  | { hostUnavailable: true }
  | { hostUnavailable: false; nextTargetId: string | undefined };

// Reads the currently selected slide in PowerPoint and returns its id. When
// the user has multiple slides selected we anchor after the last one so a
// multi-select range still inserts at the end of the range. Returns undefined
// if the host isn't available or nothing is selected.
async function getActiveSelectionTargetId(): Promise<string | undefined> {
  const PowerPoint: any = (window as any).PowerPoint;
  if (!PowerPoint?.run) return undefined;
  let targetId: string | undefined;
  try {
    await PowerPoint.run(async (context: any) => {
      const sel = context.presentation.getSelectedSlides();
      sel.load('items/id');
      await context.sync();
      if (sel.items.length > 0) {
        targetId = sel.items[sel.items.length - 1].id;
      }
    });
  } catch {
    // getSelectedSlides isn't guaranteed on older builds — fall back to
    // "insert at end" by leaving targetId undefined.
  }
  return targetId;
}

async function insertBase64IntoPresentation(
  pptxBlobUrl: string,
  targetSlideId?: string,
): Promise<InsertResult> {
  const PowerPoint: any = (window as any).PowerPoint;
  if (!PowerPoint?.run) return { hostUnavailable: true };

  const res = await fetch(pptxBlobUrl);
  if (!res.ok) throw new Error(`Failed to fetch pptx (${res.status})`);
  const buf = await res.arrayBuffer();
  let binary = '';
  const bytes = new Uint8Array(buf);
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  const base64 = btoa(binary);

  // We diff slide ids before/after the insert to identify what landed,
  // rather than assuming index math — robust against PowerPoint inserting
  // a multi-slide deck or re-ordering.
  let nextTargetId: string | undefined;
  await PowerPoint.run(async (context: any) => {
    const before = context.presentation.slides;
    before.load('items/id');
    await context.sync();
    const beforeIds = new Set<string>(before.items.map((s: any) => s.id));

    // `KeepSourceFormatting` preserves the slide's original theme, master,
    // layouts and shapes instead of re-applying the destination's theme.
    // `targetSlideId` puts new slides immediately after that slide; omitting
    // it appends at the end of the presentation (Office.js default).
    const opts: { formatting: string; targetSlideId?: string } = {
      formatting: 'KeepSourceFormatting',
    };
    if (targetSlideId) opts.targetSlideId = targetSlideId;
    context.presentation.insertSlidesFromBase64(base64, opts);

    const after = context.presentation.slides;
    after.load('items/id');
    await context.sync();

    // New slide ids are whatever wasn't in `before`. Take the last one so
    // chained inserts keep advancing forward through the deck.
    const inserted = after.items.filter((s: any) => !beforeIds.has(s.id));
    if (inserted.length > 0) {
      nextTargetId = inserted[inserted.length - 1].id;
    }
  });
  return { hostUnavailable: false, nextTargetId };
}

async function insertDeckIntoPresentation(
  deck: Deck,
  targetSlideId?: string,
): Promise<InsertResult> {
  const current = deck.versions.find(v => v.current);
  if (!current) return { hostUnavailable: false, nextTargetId: undefined };
  return insertBase64IntoPresentation(current.pptxBlobUrl, targetSlideId);
}

async function insertSlideIntoPresentation(
  slide: Slide,
  targetSlideId?: string,
): Promise<InsertResult> {
  // listDeckSlides no longer returns a usable pptxBlobUrl — every per-slide
  // download goes through /decks/{id}/slides/{slideId}/insert so the backend
  // can write an audit row and issue a short-lived (5-min) SAS.
  const { downloadUrl } = await api.requestSlideInsert(slide.deckId, slide.id);
  return insertBase64IntoPresentation(downloadUrl, targetSlideId);
}

// Bulk insert that feels like one operation.
//
// Sequential N-by-N insert was slow for two reasons:
//   1. N network round-trips (POST /insert + GET SAS-signed pptx) executed
//      one after the other.
//   2. N separate PowerPoint.run contexts, each with its own setup cost.
//
// This helper collapses (1) by firing every fetch in parallel up front
// and (2) by doing every Office.js insert inside a single PowerPoint.run
// pass. We still need one `context.sync()` between calls because each
// insert's target id depends on the previous insert's newly-created
// slide — but we save the run-context handshake.
//
// Returns how many slides actually landed. On a mid-batch insert
// failure, the partial count is returned and the caller surfaces it.
// `slides` is iterated in array order (callers pass selection order).
async function insertManyIntoPresentation(slides: Slide[]): Promise<{
  hostUnavailable: boolean;
  inserted: number;
}> {
  const PowerPoint: any = (window as any).PowerPoint;
  if (!PowerPoint?.run) return { hostUnavailable: true, inserted: 0 };
  if (slides.length === 0) return { hostUnavailable: false, inserted: 0 };

  // Phase 1 — all the network in parallel. Each item carries its base64
  // payload by the time PowerPoint sees it, so Phase 2 stays local.
  const payloads = await Promise.all(slides.map(async (s) => {
    const { downloadUrl } = await api.requestSlideInsert(s.deckId, s.id);
    const res = await fetch(downloadUrl);
    if (!res.ok) throw new Error(`Failed to fetch slide ${s.position} (${res.status})`);
    const buf = await res.arrayBuffer();
    const bytes = new Uint8Array(buf);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return { slide: s, base64: btoa(binary) };
  }));

  // Phase 2 — one PowerPoint.run, chained targetSlideIds so the stack
  // lands contiguously after the user's current PowerPoint selection.
  let inserted = 0;
  await PowerPoint.run(async (context: any) => {
    const sel = context.presentation.getSelectedSlides();
    sel.load('items/id');
    await context.sync();
    let targetId: string | undefined =
      sel.items.length > 0 ? sel.items[sel.items.length - 1].id : undefined;

    for (const { base64 } of payloads) {
      const before = context.presentation.slides;
      before.load('items/id');
      await context.sync();
      const beforeIds = new Set<string>(before.items.map((s: any) => s.id));

      const opts: { formatting: string; targetSlideId?: string } = {
        formatting: 'KeepSourceFormatting',
      };
      if (targetId) opts.targetSlideId = targetId;
      context.presentation.insertSlidesFromBase64(base64, opts);

      const after = context.presentation.slides;
      after.load('items/id');
      await context.sync();
      const newIds = after.items.filter((s: any) => !beforeIds.has(s.id));
      if (newIds.length > 0) {
        targetId = newIds[newIds.length - 1].id;
        inserted++;
      }
    }
  });
  return { hostUnavailable: false, inserted };
}

// Louk asked for a personal touch on the bulk-insert progress message.
// Picked from at start of the operation, so refresh rate ≈ once per click.
const LOUK_BUSY_MESSAGES = [
  'Louk is working hard to get all your slides inserted',
  'Louk is herding your slides into PowerPoint',
  'Louk is on it — slides incoming',
  'Louk is dragging your slides over, one at a time',
  'Louk is busy assembling your masterpiece',
];
function pickLoukMessage(): string {
  return LOUK_BUSY_MESSAGES[Math.floor(Math.random() * LOUK_BUSY_MESSAGES.length)];
}

export function SlideLibrary({ me, mode, host, branding, onBrandingChange }: SlideLibraryProps) {
  const [view, setView] = useState<View>('browse');
  // Lifted out of ManageView so notification deep-links can switch tab too.
  const [manageTab, setManageTab] = useState<ManageTab>('drafts');
  const [toast, setToast] = useState<string | null>(null);
  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3500);
  };

  // Tenant category taxonomy. Loaded once at app level; passed to Browse
  // (for filter chips), NewDraftForm + ManageView (for selects), and the
  // CategoriesPanel (for edit). refetch fires after admin saves.
  const { data: categoryTree, refetch: refetchCategories } = useApi<CategoryTree>(
    () => api.listCategories(),
    { pollMs: 5 * 60 * 1000 }, // refresh every 5 min in case another admin edits
  );

  // Notification deep-link handler — accepts the link payload from the bell
  // and switches the right surface(s) on. Manage is for Designers + Admins
  // (per-tab gating inside ManageView); viewers can't deep-link there.
  function navigate(link: NotificationLink) {
    if (link.view === 'manage' && canAuthor(me.role)) {
      setView('manage');
      if (link.tab) setManageTab(link.tab);
      return;
    }
    setView('browse');
  }

  const displayHost = mode === 'web' ? 'Web' : host;

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 flex flex-col">
      <Header me={me} host={displayHost} view={view} setView={setView} navigate={navigate} branding={branding} />

      <div className="flex-1">
        {view === 'browse' && <BrowseView me={me} mode={mode} showToast={showToast} setView={setView} categoryTree={categoryTree} />}
        {view === 'manage' && canAuthor(me.role) && (
          <ManageView
            me={me}
            mode={mode}
            showToast={showToast}
            tab={manageTab}
            setTab={setManageTab}
            onBrandingChange={onBrandingChange}
            categoryTree={categoryTree}
            onCategoriesChange={refetchCategories}
          />
        )}
        {view === 'manage' && !canAuthor(me.role) && (
          <div className="p-8 text-center text-sm text-slate-500">
            You don't have authoring permissions yet. Ask an admin to add you to the Designers group.
          </div>
        )}
        {view === 'analytics' && me.role === 'admin' && <AnalyticsView />}
        {view === 'analytics' && me.role !== 'admin' && (
          <div className="p-8 text-center text-sm text-slate-500">Admins only.</div>
        )}
      </div>

      {toast && (
        <div className="fixed bottom-4 right-4 bg-slate-800 text-white text-xs px-4 py-2 rounded shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}

// ---------- Header ----------

function Header({
  me, host, view, setView, navigate, branding,
}: {
  me: MeResponse;
  host: string;
  view: View;
  setView: (v: View) => void;
  navigate: (link: NotificationLink) => void;
  branding: Branding | null;
}) {
  // App name + initials fall back gracefully while branding is still loading.
  const appName = branding?.appName || 'Slide Library';
  const initials = appName.slice(0, 2).toUpperCase();

  return (
    <header className="bg-gradient-to-r from-accent-50 to-accent-100 border-b border-slate-200 px-3 py-2 min-[420px]:px-4 min-[420px]:py-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {branding?.logoBlobUrl ? (
            <img
              src={branding.logoBlobUrl}
              alt=""
              className="w-8 h-8 rounded object-contain bg-white border border-slate-200 flex-shrink-0"
            />
          ) : (
            <div className="w-8 h-8 rounded bg-gradient-to-br from-accent-500 to-accent-700 flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
              {initials}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-800 truncate">{appName}</div>
            {/* Identity sub-line is secondary info; on the narrowest task pane
                widths we hide it so the nav has room to render. */}
            <div className="hidden min-[420px]:block text-[10px] text-slate-500 truncate">
              {me.userPrincipalName} · {roleLabel(me.role)} · {host}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 min-[460px]:gap-2 flex-shrink-0">
          <NotificationsBell navigate={navigate} />
          {/* Browse: everyone. Manage: Designers + Admins (viewers don't
              author so the tab is hidden). Analytics: Admins only. */}
          <nav className="flex gap-0.5 min-[460px]:gap-1">
            <NavBtn active={view === 'browse'} onClick={() => setView('browse')} icon={<Layers size={14} />} label="Browse" />
            {canAuthor(me.role) && (
              <NavBtn active={view === 'manage'} onClick={() => setView('manage')} icon={<Settings size={14} />} label="Manage" />
            )}
            {me.role === 'admin' && (
              <NavBtn active={view === 'analytics'} onClick={() => setView('analytics')} icon={<BarChart3 size={14} />} label="Analytics" />
            )}
          </nav>
        </div>
      </div>
    </header>
  );
}

// ---------- Notifications bell ----------
//
// Polls /notifications every 60s. The bell shows an unread badge; the
// dropdown lists recent notifications with click-to-deep-link. The whole
// component sits inside the header so it's always visible regardless of
// view.

function NotificationsBell({ navigate }: { navigate: (link: NotificationLink) => void }) {
  const { data, refetch, setData } = useApi<{ notifications: Notification[]; unreadCount: number }>(
    () => api.listNotifications(),
    { pollMs: 60_000 },
  );
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const unread = data?.unreadCount ?? 0;
  const items = data?.notifications ?? [];

  async function markRead(id: string) {
    // Optimistic — update local state immediately so the badge reflects the
    // click even before the network round-trip lands.
    setData(prev => prev
      ? {
        notifications: prev.notifications.map(n => n.id === id ? { ...n, read: true } : n),
        unreadCount: Math.max(0, prev.unreadCount - (prev.notifications.find(n => n.id === id)?.read === false ? 1 : 0)),
      }
      : prev);
    try {
      await api.markNotificationRead(id);
    } catch {
      // Server didn't agree — re-pull to get the truth back.
      refetch();
    }
  }

  async function markAll() {
    setData(prev => prev
      ? { notifications: prev.notifications.map(n => ({ ...n, read: true })), unreadCount: 0 }
      : prev);
    try {
      await api.markAllNotificationsRead();
    } catch {
      refetch();
    }
  }

  function onItemClick(n: Notification) {
    markRead(n.id);
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="relative p-1.5 rounded hover:bg-white/60 text-slate-700"
        title="Notifications"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-accent-500 text-white text-[9px] font-semibold flex items-center justify-center">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <>
          {/* Click-outside catcher — same trick the slide menu uses. Sits
              under the panel z-wise so clicks anywhere else close it. */}
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-80 max-w-[calc(100vw-1rem)] bg-white border border-slate-200 rounded shadow-lg z-40 overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200">
              <div className="text-xs font-medium text-slate-700">
                Notifications {unread > 0 && <span className="text-slate-400">· {unread} unread</span>}
              </div>
              {unread > 0 && (
                <button onClick={markAll} className="text-[10px] text-accent-600 hover:underline">
                  Mark all read
                </button>
              )}
            </div>
            <ul className="max-h-80 overflow-y-auto divide-y divide-slate-100">
              {items.length === 0 ? (
                <li className="text-xs text-slate-400 italic p-4 text-center inline-flex flex-col items-center gap-1.5 w-full">
                  <Inbox size={18} className="text-slate-300" />
                  Your inbox is empty.
                </li>
              ) : (
                items.map(n => (
                  <li key={n.id}>
                    <button
                      onClick={() => onItemClick(n)}
                      className={`w-full text-left px-3 py-2 hover:bg-slate-50 ${!n.read ? 'bg-accent-50/60' : ''}`}
                    >
                      <div className="flex items-start gap-2">
                        {!n.read && (
                          <span className="w-1.5 h-1.5 mt-1.5 rounded-full bg-accent-500 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium text-slate-800">{n.title}</div>
                          <div className="text-[11px] text-slate-600 line-clamp-2">{n.body}</div>
                          <div className="text-[10px] text-slate-400 mt-0.5">
                            {timeAgo(n.createdAt)}
                          </div>
                        </div>
                      </div>
                    </button>
                  </li>
                ))
              )}
            </ul>
            <div className="border-t border-slate-200 px-3 py-1.5 flex justify-end">
              <button
                onClick={() => { setOpen(false); setShowSettings(true); }}
                className="text-[10px] text-slate-500 hover:text-slate-800 hover:underline inline-flex items-center gap-1"
              >
                <Settings size={10} /> Notification settings
              </button>
            </div>
          </div>
        </>
      )}

      {showSettings && (
        <NotificationSettingsModal onClose={() => setShowSettings(false)} />
      )}
    </div>
  );
}

function NotificationSettingsModal({ onClose }: { onClose: () => void }) {
  const { data, setData } = useApi<NotificationPrefs>(() => api.getNotificationPrefs());
  const [saving, setSaving] = useState<'email' | 'teams' | null>(null);

  async function toggle(channel: 'email' | 'teams') {
    if (!data) return;
    const next = { ...data, [channel]: !data[channel] };
    // Optimistic so the toggle visibly flips instantly. Roll back on error.
    setData(next);
    setSaving(channel);
    try {
      const saved = await api.updateNotificationPrefs({ [channel]: next[channel] });
      setData(saved);
    } catch {
      setData(data);
    } finally {
      setSaving(null);
    }
  }

  return (
    <Modal title="Notification settings" onClose={onClose}>
      <div className="space-y-2 text-xs">
        <p className="text-slate-600">
          Choose how you'd like to be reminded about submit, approve and reject events.
          The bell inbox is always on — these toggles control the optional channels.
        </p>
        {!data ? (
          <div className="inline-flex items-center gap-1 text-slate-500">
            <Loader2 size={11} className="animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <ToggleRow
              label="Email"
              hint="Sent from your account via Microsoft 365."
              checked={data.email}
              busy={saving === 'email'}
              onToggle={() => toggle('email')}
            />
            <ToggleRow
              label="Microsoft Teams"
              hint="Direct chat from the Slide Library app (with channel fallback if DM fails)."
              checked={data.teams}
              busy={saving === 'teams'}
              onToggle={() => toggle('teams')}
            />
            <div className="text-[10px] text-slate-400 pt-1">
              Email and Teams delivery are live. Admins can configure a Teams fallback channel under Manage → Branding for the case when 1:1 DMs can't be delivered.
            </div>
          </>
        )}
        <div className="flex justify-end pt-2">
          <button onClick={onClose} className="text-xs px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded">Done</button>
        </div>
      </div>
    </Modal>
  );
}

function ToggleRow({ label, hint, checked, busy, onToggle }: {
  label: string;
  hint: string;
  checked: boolean;
  busy: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex items-center gap-3 py-1.5">
      <button
        onClick={onToggle}
        disabled={busy}
        role="switch"
        aria-checked={checked}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${
          checked ? 'bg-accent-500' : 'bg-slate-300'
        } ${busy ? 'opacity-60' : ''}`}
      >
        <span
          className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </button>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-slate-800">{label}</div>
        <div className="text-[10px] text-slate-500">{hint}</div>
      </div>
    </div>
  );
}

// Compact relative time for the inbox. Avoids dragging in a date lib just
// for this one surface.
function timeAgo(iso: string): string {
  const now = Date.now();
  const t = new Date(iso).getTime();
  const sec = Math.max(0, Math.floor((now - t) / 1000));
  if (sec < 60) return 'just now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  if (sec < 7 * 86400) return `${Math.floor(sec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function NavBtn({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`px-1.5 min-[460px]:px-2.5 py-1 text-[11px] rounded inline-flex items-center gap-1 ${
        active ? 'bg-accent-500 text-white' : 'bg-white/60 text-slate-700 hover:bg-white'
      }`}
    >
      {icon}
      {/* Labels hide on narrow widths so all three nav buttons + bell fit. */}
      <span className="hidden min-[460px]:inline">{label}</span>
    </button>
  );
}

// ---------- Browse ----------

function BrowseView({
  me, mode, showToast, setView, categoryTree,
}: {
  me: MeResponse;
  mode: AppMode;
  showToast: (s: string) => void;
  setView: (v: View) => void;
  categoryTree: CategoryTree | null;
}) {
  const { data: decks, error } = useApi<Deck[]>(() => api.listDecks(), { pollMs: 30_000 });
  const [search, setSearch] = useState('');
  // Filter state: ALL_CATEGORY = no filter; otherwise a category slug.
  // subCategory is only meaningful when a category is selected.
  const [category, setCategory] = useState<string>(ALL_CATEGORY);
  const [subCategory, setSubCategory] = useState<string | null>(null);
  const [inserting, setInserting] = useState<string | null>(null);
  const [expandedDeckId, setExpandedDeckId] = useState<string | null>(null);

  // Reset sub-filter whenever the parent changes — picks up the new tree
  // sub-list and avoids carrying a stale slug from a different category.
  useEffect(() => { setSubCategory(null); }, [category]);

  const selectedCategoryNode = useMemo(
    () => categoryTree?.categories.find(c => c.id === category) ?? null,
    [categoryTree, category],
  );

  const knownCategoryIds = useMemo(
    () => new Set((categoryTree?.categories ?? []).map(c => c.id)),
    [categoryTree],
  );
  const visible = useMemo(() => {
    if (!decks) return [];
    return decks
      .filter(d => d.status === 'published')
      .filter(d => {
        if (category === ALL_CATEGORY) return true;
        if (category === '__other__') return !knownCategoryIds.has(d.category);
        return d.category === category;
      })
      .filter(d => !subCategory || d.subCategory === subCategory)
      .filter(d => {
        if (!search) return true;
        const s = search.toLowerCase();
        return d.title.toLowerCase().includes(s) || d.owner.toLowerCase().includes(s);
      });
  }, [decks, search, category, subCategory, knownCategoryIds]);

  // Decks whose category slug isn't (or no longer is) in the tree fall under
  // an "Other" chip so they remain reachable while admins tidy the taxonomy.
  const orphanedCategoryCount = useMemo(() => {
    if (!decks || !categoryTree) return 0;
    const known = new Set(categoryTree.categories.map(c => c.id));
    return decks.filter(d => d.status === 'published' && !known.has(d.category)).length;
  }, [decks, categoryTree]);

  const reviewCount = useMemo(() => (decks || []).filter(d => d.status === 'pending_review').length, [decks]);

  async function handleInsert(deck: Deck) {
    setInserting(deck.id);
    try {
      const targetId = await getActiveSelectionTargetId();
      const r = await insertDeckIntoPresentation(deck, targetId);
      if (r.hostUnavailable) {
        showToast('Insert not available — open in PowerPoint to use this feature');
      } else {
        await api.trackInsertion(deck.id);
        showToast(`Inserted "${deck.title}"`);
      }
    } catch (e: any) {
      showToast(`Insert failed: ${e?.message || 'unknown error'}`);
    } finally {
      setInserting(null);
    }
  }

  if (error) {
    return <div className="p-6 text-sm text-red-600">Failed to load decks: {error.message}</div>;
  }

  return (
    <div className="p-4 space-y-3">
      {/* Review queue banner removed — approval workflow deactivated.
          Designers see pending_review legacy decks in the Drafts tab and
          can publish them directly. */}
      {false && (
        <div className="bg-blue-50 border border-blue-200 rounded p-3 text-xs text-blue-900 flex items-start gap-2">
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
          <div>
            <b>{reviewCount} deck{reviewCount > 1 ? 's' : ''} pending review.</b>{' '}
            <button onClick={() => setView('manage')} className="underline font-medium">Review now →</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search decks..."
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-slate-200 rounded bg-white focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-200"
          />
        </div>
        <div className="flex gap-1 overflow-x-auto">
          <button
            key={ALL_CATEGORY}
            onClick={() => setCategory(ALL_CATEGORY)}
            className={`px-2.5 py-1 text-[11px] rounded-full whitespace-nowrap transition-colors ${
              category === ALL_CATEGORY ? 'bg-accent-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-200'
            }`}
          >
            All
          </button>
          {(categoryTree?.categories ?? []).map(c => (
            <button
              key={c.id}
              onClick={() => setCategory(c.id)}
              className={`px-2.5 py-1 text-[11px] rounded-full whitespace-nowrap transition-colors ${
                category === c.id ? 'bg-accent-500 text-white' : 'bg-white text-slate-600 hover:bg-slate-200'
              }`}
            >
              {c.name}
            </button>
          ))}
          {orphanedCategoryCount > 0 && (
            <button
              key="__other__"
              onClick={() => setCategory('__other__')}
              className={`px-2.5 py-1 text-[11px] rounded-full whitespace-nowrap transition-colors ${
                category === '__other__' ? 'bg-accent-500 text-white' : 'bg-white text-slate-500 hover:bg-slate-200 italic'
              }`}
              title="Decks whose category isn't in the current tree"
            >
              Other ({orphanedCategoryCount})
            </button>
          )}
        </div>

        {/* Second row of sub-category pills — only when the selected
            category has children. Picking one narrows further; picking the
            already-selected one clears the sub-filter. */}
        {selectedCategoryNode && selectedCategoryNode.subCategories.length > 0 && (
          <div className="flex gap-1 overflow-x-auto pl-1">
            {selectedCategoryNode.subCategories.map(s => (
              <button
                key={s.id}
                onClick={() => setSubCategory(prev => prev === s.id ? null : s.id)}
                className={`px-2 py-0.5 text-[10px] rounded-full whitespace-nowrap transition-colors ${
                  subCategory === s.id
                    ? 'bg-accent-100 text-accent-800 border border-accent-300'
                    : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'
                }`}
              >
                {s.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {!decks ? (
        <div className="text-sm text-slate-500 p-4">Loading decks…</div>
      ) : visible.length === 0 ? (
        <div className="text-sm text-slate-500 p-8 text-center bg-white rounded border border-slate-200">
          No decks match your filters.
          {decks.length === 0 && canAuthor(me.role) && (
            <div className="mt-2">
              <button onClick={() => setView('manage')} className="text-accent-600 underline">
                Create your first deck →
              </button>
            </div>
          )}
        </div>
      ) : (
        <ul className="space-y-2">
          {visible.map(d => (
            <li key={d.id} className="bg-white border border-slate-200 rounded overflow-hidden">
              <div className="p-3 flex items-start gap-3">
                <DeckCover deck={d} className="w-12 h-9 rounded flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{d.title}</div>
                  <div className="text-[10px] text-slate-500 flex items-center gap-2 mt-0.5">
                    <span>{d.slideCount} slides</span><span>·</span>
                    <span className="truncate">{d.owner}</span><span>·</span>
                    <span>
                      {categoryLabel(categoryTree, d.category)}
                      {d.subCategory && (
                        <> / {subCategoryLabel(categoryTree, d.category, d.subCategory)}</>
                      )}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setExpandedDeckId(expandedDeckId === d.id ? null : d.id)}
                    title={expandedDeckId === d.id ? 'Hide slides' : 'Browse individual slides'}
                    className="p-1.5 text-slate-500 hover:bg-slate-100 rounded"
                  >
                    {expandedDeckId === d.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </button>
                  {mode === 'office' ? (
                    <button
                      onClick={() => handleInsert(d)}
                      disabled={!d.versions.some(v => v.current) || inserting === d.id}
                      className="px-3 py-1.5 text-xs bg-accent-500 hover:bg-accent-600 disabled:bg-slate-300 text-white rounded font-medium inline-flex items-center gap-1"
                    >
                      <Plus size={12} /> {inserting === d.id ? 'Inserting…' : 'Insert'}
                    </button>
                  ) : (
                    <span
                      className="px-3 py-1.5 text-xs bg-slate-100 text-slate-500 rounded inline-flex items-center gap-1"
                      title="Open in PowerPoint to insert this deck"
                    >
                      PowerPoint only
                    </span>
                  )}
                </div>
              </div>
              {expandedDeckId === d.id && (
                <SlideGrid deck={d} mode={mode} showToast={showToast} canManage={canAuthor(me.role)} />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------- Browse: per-deck slide grid (multi-select, no delete/rename) ----------

function SlideGrid({ deck, mode, showToast, canManage }: {
  deck: Deck;
  mode: AppMode;
  showToast: (s: string) => void;
  /** Designer/Admin can re-process; viewers see a passive message. */
  canManage: boolean;
}) {
  const deckId = deck.id;
  const { data: slides, loading, error, refetch } = useApi<Slide[]>(() => api.listDeckSlides(deckId), { deps: [deckId] });
  const [insertingId, setInsertingId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [insertingMsg, setInsertingMsg] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const [reprocessing, setReprocessing] = useState(false);

  const isEmpty = !loading && !error && (!slides || slides.length === 0);

  // Anchor the "how long has this been empty?" clock to when the deck's
  // current version was uploaded — NOT to when the user expanded the row.
  // A deck uploaded an hour ago with zero slides isn't "still processing":
  // either the split worker failed for that version, or a designer deleted
  // all the slides afterwards. Using `openedAt` led to a misleading
  // "Processing this deck… 6s elapsed" message in both cases.
  const currentVersion = deck.versions.find(v => v.current) ?? deck.versions[0];
  const versionPublishedAt = currentVersion
    ? new Date(currentVersion.publishedAt).getTime()
    : Date.now();

  // While slides are empty, poll listDeckSlides every 5s and tick the clock
  // every second so the "waited Ns" display stays live.
  useEffect(() => {
    if (!isEmpty) return;
    const tickId = setInterval(() => setNow(Date.now()), 1000);
    const pollId = setInterval(() => refetch(), 5000);
    return () => {
      clearInterval(tickId);
      clearInterval(pollId);
    };
  }, [isEmpty, refetch]);

  async function reprocess() {
    setReprocessing(true);
    try {
      await api.reprocessDeck(deckId);
      showToast('Reprocessing queued — slides will reappear shortly.');
      // Force the poll to pick up the new state immediately.
      refetch();
    } catch (e: any) {
      const msg = e instanceof ApiError ? `${e.status} ${e.message}` : e?.message || 'Reprocess failed';
      showToast(`Reprocess failed: ${msg}`);
    } finally {
      setReprocessing(false);
    }
  }

  function toggle(slideId: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(slideId)) next.delete(slideId);
      else next.add(slideId);
      return next;
    });
  }
  function selectAll() {
    if (!slides) return;
    setSelected(new Set(slides.map(s => s.id)));
  }
  function clearSelection() { setSelected(new Set()); }

  async function handleInsertSlide(slide: Slide) {
    setInsertingId(slide.id);
    try {
      const targetId = await getActiveSelectionTargetId();
      const r = await insertSlideIntoPresentation(slide, targetId);
      if (r.hostUnavailable) {
        showToast('Insert not available — open in PowerPoint to use this feature');
      } else {
        await api.trackInsertion(slide.deckId, slide.id);
        showToast(`Inserted slide ${slide.position}`);
      }
    } catch (e: any) {
      showToast(`Insert failed: ${e?.message || 'unknown'}`);
    } finally {
      setInsertingId(null);
    }
  }

  async function insertSelected() {
    if (!slides || mode !== 'office') return;
    // Iterate in selection order (Set insertion order), not deck order, so
    // the destination stack reflects how the user clicked the thumbnails.
    const byId = new Map(slides.map(s => [s.id, s]));
    const order = Array.from(selected)
      .map(id => byId.get(id))
      .filter((s): s is Slide => !!s);

    setInsertingMsg(pickLoukMessage());
    try {
      const { hostUnavailable, inserted } = await insertManyIntoPresentation(order);
      if (hostUnavailable) {
        showToast('Insert not available — open in PowerPoint to use this feature');
        return;
      }
      // Analytics fire in parallel after the inserts land — fire-and-forget
      // so a single tracking miss doesn't block the toast.
      Promise.all(order.slice(0, inserted).map(s =>
        api.trackInsertion(s.deckId, s.id).catch(() => undefined),
      ));
      showToast(`Inserted ${inserted}/${order.length} slide${order.length === 1 ? '' : 's'}`);
      setSelected(new Set());
    } catch (e: any) {
      showToast(`Insert failed: ${e?.message || 'unknown'}`);
    } finally {
      setInsertingMsg(null);
    }
  }

  if (loading) {
    return (
      <div className="px-3 pb-3 text-xs text-slate-500 flex items-center gap-2">
        <Loader2 size={12} className="animate-spin" /> Loading slides…
      </div>
    );
  }
  if (error) {
    return (
      <div className="px-3 pb-3 text-xs text-rose-600 flex items-center gap-2">
        <AlertCircle size={12} /> Failed to load slides: {error.message}
        <button onClick={() => refetch()} className="ml-2 underline hover:text-rose-800">retry</button>
      </div>
    );
  }
  if (isEmpty) {
    // Three regimes based on time since the version was uploaded:
    //   - No version at all: deck row shouldn't even show — guard anyway.
    //   - Within 5 min of upload: genuinely processing — animated spinner.
    //   - Older than 5 min: either the worker failed or all slides were
    //     deleted afterwards. Surface a recovery action instead of pretending
    //     work is still in progress.
    if (!currentVersion) {
      return (
        <div className="border-t border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-500">
          No file uploaded for this deck yet.
        </div>
      );
    }
    const elapsedSinceUpload = now - versionPublishedAt;
    const stalled = elapsedSinceUpload > 5 * 60 * 1000;
    const elapsedSec = Math.floor(elapsedSinceUpload / 1000);

    if (stalled) {
      return (
        <div className="border-t border-slate-200 bg-amber-50 px-3 py-3">
          <div className="flex items-center gap-3">
            <AlertCircle size={16} className="text-amber-600" />
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-slate-800">
                Deck has no slides
              </div>
              <div className="text-[10px] text-slate-600 mt-0.5">
                {currentVersion.v} was uploaded {Math.floor(elapsedSec / 60)} min ago but the slide library is empty.
                Either the slide-split worker failed for this version, or all slides were deleted afterwards.
                {canManage
                  ? ' Click Re-process to re-run the worker on the existing file, or re-upload from Manage → Decks.'
                  : ' Ask a designer to re-process or re-upload this deck.'}
              </div>
            </div>
            {canManage && (
              <button
                onClick={reprocess}
                disabled={reprocessing}
                className="text-[11px] px-2 py-1 bg-white border border-amber-300 hover:bg-amber-100 text-amber-800 rounded inline-flex items-center gap-1 disabled:opacity-50"
                title="Re-enqueue the slide-split worker for the current version"
              >
                {reprocessing ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                Re-process
              </button>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="border-t border-slate-200 bg-slate-50 px-3 py-3">
        <div className="flex items-center gap-3">
          <Loader2 size={16} className="animate-spin text-accent-500" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium text-slate-800">Processing this deck…</div>
            <div className="text-[10px] text-slate-500 mt-0.5">
              Slide-split worker is converting + thumbnailing. Auto-checking every 5 seconds ({elapsedSec}s since upload).
            </div>
          </div>
          <button
            onClick={() => refetch()}
            className="text-[11px] px-2 py-1 bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 rounded inline-flex items-center gap-1"
            title="Refresh now"
          >
            <RefreshCw size={11} /> Refresh
          </button>
        </div>
      </div>
    );
  }

  if (!slides) return null;

  const selectedCount = selected.size;

  return (
    <div className="border-t border-slate-200 bg-slate-50 px-3 py-3">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-[11px] text-slate-600">
          {selectedCount > 0 ? `${selectedCount} selected` : `${slides.length} slides`}
        </span>
        <button onClick={selectAll} className="text-[11px] px-2 py-0.5 bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 rounded">
          Select all
        </button>
        <button
          onClick={clearSelection}
          disabled={selectedCount === 0}
          className="text-[11px] px-2 py-0.5 bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-50 text-slate-700 rounded"
        >
          Clear
        </button>
        <div className="flex-1" />
        {mode === 'office' && (
          <button
            onClick={insertSelected}
            disabled={selectedCount === 0 || insertingMsg !== null}
            className="text-[11px] px-2.5 py-1 bg-accent-500 hover:bg-accent-600 disabled:bg-slate-300 text-white rounded inline-flex items-center gap-1"
          >
            <Plus size={11} />
            {selectedCount > 0 ? `Insert ${selectedCount} selected` : 'Insert selected'}
          </button>
        )}
      </div>

      {insertingMsg && <LoukBanner message={insertingMsg} />}

      <div className="grid grid-cols-1 min-[300px]:grid-cols-2 min-[500px]:grid-cols-3 gap-2">

        {slides.map(s => {
          const isSelected = selected.has(s.id);
          const busy = insertingId === s.id || insertingMsg !== null;
          return (
            <div
              key={s.id}
              className={`group relative bg-white border rounded overflow-hidden transition-colors ${
                isSelected ? 'border-accent-500 ring-2 ring-accent-200' : 'border-slate-200 hover:border-accent-400'
              } ${busy ? 'opacity-60' : ''}`}
            >
              <button
                onClick={(e) => { e.stopPropagation(); toggle(s.id); }}
                className={`absolute top-1 left-1 z-10 w-5 h-5 rounded border-2 flex items-center justify-center text-white text-[10px] ${
                  isSelected ? 'bg-accent-500 border-accent-500' : 'bg-white/90 border-slate-300 hover:border-accent-400'
                }`}
                title={isSelected ? 'Deselect' : 'Select'}
              >
                {isSelected ? '✓' : ''}
              </button>
              <button
                onClick={() => {
                  if (mode !== 'office') {
                    showToast('Open in PowerPoint to insert this slide');
                    return;
                  }
                  // If the user has built a selection, the thumbnail click toggles
                  // selection instead of single-inserting — avoids two inputs fighting.
                  if (selectedCount > 0) {
                    toggle(s.id);
                  } else {
                    handleInsertSlide(s);
                  }
                }}
                disabled={busy}
                title={`Slide ${s.position}${s.title ? `: ${s.title}` : ''}`}
                className="block w-full text-left disabled:cursor-not-allowed"
              >
                <div className="aspect-[4/3] bg-slate-100">
                  <img src={s.thumbnailBlobUrl} alt={`Slide ${s.position}`} className="w-full h-full object-contain" loading="lazy" />
                </div>
                <div className="p-1.5 text-[10px] text-slate-700 truncate">
                  <span className="font-medium">{s.position}.</span> <span>{s.title || 'Untitled'}</span>
                </div>
                {mode === 'office' && selectedCount === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    <span className="bg-accent-500 text-white text-[10px] px-2 py-1 rounded font-medium shadow">
                      {insertingId === s.id ? 'Inserting…' : 'Insert this slide'}
                    </span>
                  </div>
                )}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ManageView({
  me, mode, showToast, tab, setTab, onBrandingChange, categoryTree, onCategoriesChange,
}: {
  me: MeResponse;
  mode: AppMode;
  showToast: (s: string) => void;
  tab: ManageTab;
  setTab: (t: ManageTab) => void;
  onBrandingChange: () => void;
  categoryTree: CategoryTree | null;
  onCategoriesChange: () => void;
}) {
  const decksApi = useApi<Deck[]>(() => api.listDecks(), { pollMs: 30_000 });
  const decks = decksApi.data || [];

  // With the approval workflow deactivated, role-based tab visibility is
  // just Designer vs Admin:
  //   Designer (contributor / curator): Drafts + Slides
  //   Admin    : + Curators + Categories + Branding + Notifications
  // Review queue is hidden — designers publish directly from Drafts.
  const showReview = false;
  const showSlides = true;
  const showAdminTabs = me.role === 'admin';

  // Coerce to an allowed tab if the current selection isn't permitted by
  // this role.
  useEffect(() => {
    if (tab === 'review' && !showReview) setTab('drafts');
    if (tab === 'slides' && !showSlides) setTab('drafts');
    if ((tab === 'curators' || tab === 'categories' || tab === 'branding' || tab === 'notifications') && !showAdminTabs) setTab('drafts');
  }, [tab, showReview, showSlides, showAdminTabs, setTab]);

  // Decks counter: every deck in the tenant (drafts + published). Designers
  // and admins manage them all from the Decks tab — including delete, which
  // was previously gated behind Drafts-only visibility.
  const deckCount = decks.length;

  return (
    <div className="p-4 space-y-3">
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        <ManageTabBtn active={tab === 'drafts'} onClick={() => setTab('drafts')}>
          Decks ({deckCount})
        </ManageTabBtn>
        {showReview && (
          <ManageTabBtn active={tab === 'review'} onClick={() => setTab('review')}>
            Review queue ({decks.filter(d => d.status === 'pending_review').length})
          </ManageTabBtn>
        )}
        {showSlides && (
          <ManageTabBtn active={tab === 'slides'} onClick={() => setTab('slides')}>
            Slides
          </ManageTabBtn>
        )}
        {showAdminTabs && (
          <ManageTabBtn active={tab === 'curators'} onClick={() => setTab('curators')}>
            Curators
          </ManageTabBtn>
        )}
        {showAdminTabs && (
          <ManageTabBtn active={tab === 'categories'} onClick={() => setTab('categories')}>
            Categories
          </ManageTabBtn>
        )}
        {showAdminTabs && (
          <ManageTabBtn active={tab === 'notifications'} onClick={() => setTab('notifications')}>
            Notifications
          </ManageTabBtn>
        )}
        {showAdminTabs && (
          <ManageTabBtn active={tab === 'branding'} onClick={() => setTab('branding')}>
            Branding
          </ManageTabBtn>
        )}
      </div>

      {tab === 'drafts' && <DraftsTab me={me} decks={decks} refetch={decksApi.refetch} showToast={showToast} categoryTree={categoryTree} />}
      {tab === 'review' && showReview && <ReviewTab decks={decks} refetch={decksApi.refetch} showToast={showToast} />}
      {tab === 'slides' && showSlides && <SlidesTab decks={decks} refetch={decksApi.refetch} mode={mode} showToast={showToast} />}
      {tab === 'curators' && showAdminTabs && <CuratorsTab showToast={showToast} />}
      {tab === 'categories' && showAdminTabs && <CategoriesPanel tree={categoryTree} onSaved={onCategoriesChange} showToast={showToast} />}
      {tab === 'notifications' && showAdminTabs && <NotificationTemplatesTab showToast={showToast} />}
      {tab === 'branding' && showAdminTabs && <BrandingTab showToast={showToast} onBrandingChange={onBrandingChange} />}
    </div>
  );
}

function ManageTabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 text-xs font-medium border-b-2 ${
        active ? 'border-accent-500 text-accent-700' : 'border-transparent text-slate-600 hover:text-slate-800'
      }`}
    >
      {children}
    </button>
  );
}

function DraftsTab({
  me, decks, refetch, showToast, categoryTree,
}: {
  me: MeResponse;
  decks: Deck[]; refetch: () => void; showToast: (s: string) => void; categoryTree: CategoryTree | null;
}) {
  // What this tab shows now that the approval workflow is deactivated:
  // EVERY deck in the tenant — drafts, in-review (legacy), and published.
  // The status pill on each row distinguishes them. Row-level gates
  // (canEdit / canPublishDirect / canDelete) decide which buttons render
  // per deck; published decks show Delete only, drafts show the full set.
  // This is the single management surface for designers + admins.
  const drafts = decks;
  const [showNew, setShowNew] = useState(false);
  const [uploadingFor, setUploadingFor] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Deck | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function saveTitle(deck: Deck, newTitle: string) {
    const trimmed = newTitle.trim();
    if (!trimmed || trimmed === deck.title) {
      setRenamingId(null);
      return;
    }
    try {
      await api.renameDeck(deck.id, trimmed);
      showToast(`Renamed to "${trimmed}"`);
      refetch();
    } catch (e: any) {
      const msg = e instanceof ApiError ? `${e.status} ${e.message}` : e?.message || 'Rename failed';
      showToast(`Rename failed: ${msg}`);
    } finally {
      setRenamingId(null);
    }
  }

  async function performDelete(deck: Deck) {
    setDeleting(true);
    try {
      await api.deleteDeck(deck.id);
      showToast(`Deleted "${deck.title}"`);
      refetch();
    } catch (e: any) {
      const msg = e instanceof ApiError ? `${e.status} ${e.message}` : e?.message || 'Delete failed';
      showToast(`Delete failed: ${msg}`);
    } finally {
      setDeleting(false);
      setConfirmDelete(null);
    }
  }

  async function uploadFile(deckId: string, file: File) {
    setUploadingFor(deckId);
    try {
      await api.uploadDeck(deckId, file);
      showToast('Upload successful');
      refetch();
    } catch (e: any) {
      const msg = e instanceof ApiError ? `${e.status} ${e.message}` : e?.message || 'Upload failed';
      showToast(`Upload failed: ${msg}`);
    } finally {
      setUploadingFor(null);
    }
  }


  async function publish(deck: Deck) {
    try {
      await api.publishDeck(deck.id);
      showToast(`"${deck.title}" published`);
      refetch();
    } catch (e: any) {
      showToast(`Publish failed: ${e?.message || 'unknown'}`);
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <button
          onClick={() => setShowNew(s => !s)}
          className="text-xs px-3 py-1.5 bg-accent-500 hover:bg-accent-600 text-white rounded inline-flex items-center gap-1"
        >
          <Plus size={12} /> New draft
        </button>
      </div>

      {showNew && <NewDraftForm onCreated={() => { setShowNew(false); refetch(); }} showToast={showToast} categoryTree={categoryTree} />}

      {drafts.length === 0 ? (
        <div className="text-sm text-slate-500 p-6 text-center bg-white rounded border border-slate-200">
          No decks yet. Click "New draft" to start one.
        </div>
      ) : (
        <ul className="space-y-2">
          {drafts.map(d => {
            // Action affordances after the approval workflow was deactivated:
            //   - Any Designer (role >= contributor) can edit + publish any
            //     non-published deck. No ownership / status nuance.
            //   - Whole-deck Delete: designers + admins (viewers blocked).
            //   - Per-deck group restrictions stay admin-only (tenant policy).
            const isUnpublished = d.status !== 'published';
            const canEdit = isUnpublished;
            const canPublishDirect = isUnpublished;
            const canDelete = canAuthor(me.role);
            const canSetGroups = me.role === 'admin';

            return (
              <li key={d.id} className="bg-white border border-slate-200 rounded p-3">
                <div className="flex items-start gap-3">
                  <DeckCover deck={d} className="w-10 h-7 rounded flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    {canEdit && renamingId === d.id ? (
                      <input
                        autoFocus
                        defaultValue={d.title}
                        maxLength={120}
                        onBlur={e => saveTitle(d, e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                          if (e.key === 'Escape') setRenamingId(null);
                        }}
                        className="w-full px-1.5 py-0.5 text-sm font-medium border border-accent-300 rounded focus:outline-none focus:border-accent-500"
                      />
                    ) : canEdit ? (
                      <button
                        onClick={() => setRenamingId(d.id)}
                        className="text-sm font-medium text-slate-800 truncate text-left hover:underline w-full"
                        title="Click to rename"
                      >
                        {d.title}
                      </button>
                    ) : (
                      <div className="text-sm font-medium text-slate-800 truncate">{d.title}</div>
                    )}
                    <div className="text-[10px] text-slate-500 flex items-center gap-1.5 flex-wrap">
                      <StatusPill status={d.status} />
                      <span>{d.category}</span>
                      <span>·</span>
                      <span className="truncate">{d.owner}</span>
                      <span>·</span>
                      <span>{d.versions.length} version{d.versions.length === 1 ? '' : 's'}</span>
                    </div>
                    {canSetGroups && (
                      <div className="mt-1">
                        <GroupRestrictionsButton deck={d} onUpdated={() => refetch()} showToast={showToast} />
                      </div>
                    )}
                    {d.rejectionReason && d.status === 'draft' && (
                      <div className="mt-1 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded p-1.5">
                        Sent back: {d.rejectionReason}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    {canEdit && (
                      <label className="text-[11px] px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded inline-flex items-center gap-1 cursor-pointer">
                        <Upload size={11} /> {uploadingFor === d.id ? 'Uploading…' : 'Upload .pptx/.potx'}
                        <input
                          type="file"
                          accept=".pptx,.potx,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.openxmlformats-officedocument.presentationml.template"
                          className="hidden"
                          disabled={uploadingFor === d.id}
                          onChange={e => {
                            const f = e.target.files?.[0];
                            if (f) uploadFile(d.id, f);
                            e.target.value = '';
                          }}
                        />
                      </label>
                    )}
                    {/* Approval workflow deactivated — no Submit button.
                        Designers click Publish directly from Drafts. */}
                    {canPublishDirect && (
                      <button
                        onClick={() => publish(d)}
                        disabled={d.versions.length === 0}
                        className="text-[11px] px-2 py-1 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-300 text-white rounded inline-flex items-center gap-1"
                      >
                        <ThumbsUp size={11} /> Publish now
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => setConfirmDelete(d)}
                        className="text-[11px] px-2 py-1 bg-rose-50 hover:bg-rose-100 text-rose-700 rounded inline-flex items-center gap-1"
                      >
                        <Trash2 size={11} /> Delete
                      </button>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {confirmDelete && (
        <ConfirmDialog
          title={`Delete "${confirmDelete.title}"?`}
          message={
            `This permanently removes the deck, its ${confirmDelete.versions.length} version(s), all per-slide files, and ${confirmDelete.slideCount} slide record(s).\n\nThis cannot be undone.`
          }
          confirmLabel="Delete deck"
          danger
          busy={deleting}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => performDelete(confirmDelete)}
        />
      )}
    </div>
  );
}

function NewDraftForm({
  onCreated, showToast, categoryTree,
}: {
  onCreated: () => void; showToast: (s: string) => void; categoryTree: CategoryTree | null;
}) {
  const [title, setTitle] = useState('');
  const firstCategorySlug = categoryTree?.categories[0]?.id ?? '';
  const [category, setCategory] = useState<string>(firstCategorySlug);
  const [subCategory, setSubCategory] = useState<string>('');
  const [owner, setOwner] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [stage, setStage] = useState<'idle' | 'creating' | 'uploading'>('idle');

  // Default to the first category once the tree loads; reset sub when
  // category changes so we never submit a stale sub from a different parent.
  useEffect(() => {
    if (!category && firstCategorySlug) setCategory(firstCategorySlug);
  }, [firstCategorySlug, category]);
  useEffect(() => { setSubCategory(''); }, [category]);

  const selectedCat = categoryTree?.categories.find(c => c.id === category);
  const hasSubs = !!selectedCat && selectedCat.subCategories.length > 0;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim()) return;
    if (!category) {
      showToast('Pick a category — none configured. Ask an admin to set them up in Manage → Categories.');
      return;
    }
    // Chain create → upload so the curator gets a usable deck in one step.
    // If the upload half fails, the empty draft still exists — curator can
    // retry from the row's existing "Upload .pptx/.potx" button.
    setStage('creating');
    let deck;
    try {
      deck = await api.createDeck({
        title: title.trim(),
        category,
        ...(subCategory ? { subCategory } : {}),
        owner: owner.trim(),
      });
    } catch (e: any) {
      const msg = e instanceof ApiError ? `${e.status} ${e.message}` : e?.message || 'Create failed';
      showToast(`Create failed: ${msg}`);
      setStage('idle');
      return;
    }

    if (!file) {
      showToast(`Draft "${title}" created`);
      setStage('idle');
      onCreated();
      return;
    }

    setStage('uploading');
    try {
      await api.uploadDeck(deck.id, file);
      showToast(`Draft "${title}" created and uploaded`);
      onCreated();
    } catch (e: any) {
      const msg = e instanceof ApiError ? `${e.status} ${e.message}` : e?.message || 'Upload failed';
      // Surface the half-success explicitly so the curator knows the draft
      // exists but needs re-upload from the row.
      showToast(`Draft created but upload failed (${msg}). Use the row's upload button to retry.`);
      onCreated();
    } finally {
      setStage('idle');
    }
  }

  const busy = stage !== 'idle';
  const submitLabel =
    stage === 'creating' ? 'Creating…' :
    stage === 'uploading' ? 'Uploading…' :
    file ? 'Create + upload' : 'Create draft';

  return (
    <form onSubmit={submit} className="bg-white border border-slate-200 rounded p-3 space-y-2">
      <input
        value={title}
        onChange={e => setTitle(e.target.value)}
        placeholder="Deck title"
        maxLength={120}
        className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-accent-400"
      />
      <div className="flex gap-2">
        <select
          value={category}
          onChange={e => setCategory(e.target.value)}
          className="flex-1 px-2 py-1.5 text-sm border border-slate-200 rounded"
        >
          {(categoryTree?.categories ?? []).map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
          {!categoryTree?.categories.length && <option value="">No categories — ask an admin</option>}
        </select>
        <select
          value={subCategory}
          onChange={e => setSubCategory(e.target.value)}
          disabled={!hasSubs}
          className="flex-1 px-2 py-1.5 text-sm border border-slate-200 rounded disabled:bg-slate-50 disabled:text-slate-400"
        >
          <option value="">{hasSubs ? '(no sub-category)' : '—'}</option>
          {selectedCat?.subCategories.map(s => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>
      <input
        value={owner}
        onChange={e => setOwner(e.target.value)}
        placeholder="Owner (team)"
        className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-accent-400"
      />
      <div className="flex items-center gap-2">
        <label className="text-[11px] px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded inline-flex items-center gap-1 cursor-pointer flex-shrink-0">
          <Upload size={11} /> {file ? 'Change file' : 'Attach .pptx/.potx (optional)'}
          <input
            type="file"
            accept=".pptx,.potx,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.openxmlformats-officedocument.presentationml.template"
            className="hidden"
            disabled={busy}
            onChange={e => setFile(e.target.files?.[0] || null)}
          />
        </label>
        {file && (
          <>
            <span className="text-[10px] text-slate-600 truncate" title={file.name}>{file.name}</span>
            <button
              type="button"
              onClick={() => setFile(null)}
              disabled={busy}
              className="text-slate-400 hover:text-slate-700 flex-shrink-0"
              title="Remove file"
            >
              <X size={12} />
            </button>
          </>
        )}
      </div>
      <button
        type="submit"
        disabled={busy || !title.trim()}
        className="text-xs px-3 py-1.5 bg-accent-500 hover:bg-accent-600 disabled:bg-slate-300 text-white rounded inline-flex items-center gap-1"
      >
        {busy && <Loader2 size={11} className="animate-spin" />} {submitLabel}
      </button>
    </form>
  );
}

function ReviewTab({ decks, refetch, showToast }: { decks: Deck[]; refetch: () => void; showToast: (s: string) => void }) {
  const pending = decks.filter(d => d.status === 'pending_review');
  const [rejecting, setRejecting] = useState<Deck | null>(null);
  const [reason, setReason] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<Deck | null>(null);
  const [deleting, setDeleting] = useState(false);

  async function approve(d: Deck) {
    try {
      await api.publishDeck(d.id);
      showToast(`"${d.title}" published`);
      refetch();
    } catch (e: any) {
      showToast(`Approve failed: ${e?.message || 'unknown'}`);
    }
  }

  async function reject() {
    if (!rejecting || !reason.trim()) return;
    try {
      await api.rejectDeck(rejecting.id, reason.trim());
      showToast(`"${rejecting.title}" sent back with feedback`);
      setRejecting(null);
      setReason('');
      refetch();
    } catch (e: any) {
      showToast(`Reject failed: ${e?.message || 'unknown'}`);
    }
  }

  async function performDelete(deck: Deck) {
    setDeleting(true);
    try {
      await api.deleteDeck(deck.id);
      showToast(`Deleted "${deck.title}"`);
      refetch();
    } catch (e: any) {
      const msg = e instanceof ApiError ? `${e.status} ${e.message}` : e?.message || 'Delete failed';
      showToast(`Delete failed: ${msg}`);
    } finally {
      setDeleting(false);
      setConfirmDelete(null);
    }
  }

  if (pending.length === 0) {
    return (
      <div className="text-sm text-slate-500 p-6 text-center bg-white rounded border border-slate-200">
        No decks waiting for review.
      </div>
    );
  }

  return (
    <>
      <ul className="space-y-2">
        {pending.map(d => (
          <li key={d.id} className="bg-white border border-slate-200 rounded p-3">
            <div className="flex items-start gap-3">
              <DeckCover deck={d} className="w-10 h-7 rounded flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">{d.title}</div>
                <div className="text-[10px] text-slate-500 flex items-center gap-1.5">
                  <Clock size={10} />
                  Submitted {d.submittedAt ? new Date(d.submittedAt).toLocaleDateString() : '—'} · {d.versions.length} version{d.versions.length === 1 ? '' : 's'}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => approve(d)}
                  className="text-[11px] px-2 py-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded inline-flex items-center gap-1"
                >
                  <ThumbsUp size={11} /> Approve
                </button>
                <button
                  onClick={() => { setRejecting(d); setReason(''); }}
                  className="text-[11px] px-2 py-1 bg-rose-500 hover:bg-rose-600 text-white rounded inline-flex items-center gap-1"
                >
                  <ThumbsDown size={11} /> Reject
                </button>
                <button
                  onClick={() => setConfirmDelete(d)}
                  className="text-[11px] px-2 py-1 bg-rose-50 hover:bg-rose-100 text-rose-700 rounded inline-flex items-center gap-1"
                >
                  <Trash2 size={11} /> Delete
                </button>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {confirmDelete && (
        <ConfirmDialog
          title={`Delete "${confirmDelete.title}"?`}
          message={
            `This permanently removes the deck, its ${confirmDelete.versions.length} version(s), all per-slide files, and ${confirmDelete.slideCount} slide record(s).\n\nThis cannot be undone.`
          }
          confirmLabel="Delete deck"
          danger
          busy={deleting}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => performDelete(confirmDelete)}
        />
      )}

      {rejecting && (
        <Modal onClose={() => setRejecting(null)} title={`Reject "${rejecting.title}"`}>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="Feedback for the curator (required)…"
            rows={4}
            className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-accent-400"
          />
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => setRejecting(null)} className="text-xs px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded">
              Cancel
            </button>
            <button
              onClick={reject}
              disabled={!reason.trim()}
              className="text-xs px-3 py-1.5 bg-rose-500 hover:bg-rose-600 disabled:bg-slate-300 text-white rounded"
            >
              Send back
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

// ---------- Manage > Slides ----------
// Curator UX for managing a deck's slides after the worker has split them:
// multi-select + insert-many, per-slide delete, inline rename. Reorder is
// deliberately out of scope until we decouple position from the Slide doc's
// deterministic ID (worker writes `${deckId}-${version}-${position}` today,
// so changing position would either collide or orphan the blob).

function SlidesTab({ decks, refetch, mode, showToast }: { decks: Deck[]; refetch: () => void; mode: AppMode; showToast: (s: string) => void }) {
  const [selectedDeckId, setSelectedDeckId] = useState<string | null>(null);
  const selectedDeck = decks.find(d => d.id === selectedDeckId) || null;

  if (!selectedDeck) {
    const ready = decks.filter(d => d.slideCount > 0);
    return (
      <div>
        <div className="text-xs text-slate-500 mb-2">Pick a deck to manage its slides.</div>
        {ready.length === 0 ? (
          <div className="text-sm text-slate-500 p-6 text-center bg-white rounded border border-slate-200">
            No decks with slide data yet. Upload one in <b>Drafts</b> first.
          </div>
        ) : (
          <ul className="space-y-2">
            {ready.map(d => (
              <li key={d.id}>
                <button
                  onClick={() => setSelectedDeckId(d.id)}
                  className="w-full bg-white border border-slate-200 rounded p-3 flex items-center gap-3 hover:border-accent-400 text-left"
                >
                  <DeckCover deck={d} className="w-10 h-7 rounded flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{d.title}</div>
                    <div className="text-[10px] text-slate-500">
                      {d.slideCount} slides · {d.category} · {d.status}
                    </div>
                  </div>
                  <ChevronDown size={14} className="-rotate-90 text-slate-400" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div>
      <button
        onClick={() => setSelectedDeckId(null)}
        className="text-[11px] px-2 py-1 mb-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded inline-flex items-center gap-1"
      >
        ← All decks
      </button>
      <div className="mb-3 flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-800 truncate">{selectedDeck.title}</div>
          <div className="text-[10px] text-slate-500">
            {selectedDeck.slideCount} slides · {selectedDeck.category} · {selectedDeck.owner} · {selectedDeck.status}
          </div>
        </div>
        <div className="flex-shrink-0">
          <GroupRestrictionsButton
            deck={selectedDeck}
            variant="inline"
            onUpdated={() => refetch()}
            showToast={showToast}
          />
        </div>
      </div>
      <ManageSlidesGrid deckId={selectedDeck.id} mode={mode} showToast={showToast} />
    </div>
  );
}

function ManageSlidesGrid({ deckId, mode, showToast }: { deckId: string; mode: AppMode; showToast: (s: string) => void }) {
  const { data: slides, loading, error, refetch, setData } = useApi<Slide[]>(
    () => api.listDeckSlides(deckId),
    { deps: [deckId] }
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busyDelete, setBusyDelete] = useState(false);
  const [insertingMsg, setInsertingMsg] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [confirmDeleteN, setConfirmDeleteN] = useState(false);

  function toggle(slideId: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(slideId)) next.delete(slideId);
      else next.add(slideId);
      return next;
    });
  }

  function selectAll() {
    if (!slides) return;
    setSelected(new Set(slides.map(s => s.id)));
  }
  function clearSelection() { setSelected(new Set()); }

  async function insertSelected() {
    if (!slides || mode !== 'office') return;
    // Selection order (Set insertion order), not deck order.
    const byId = new Map(slides.map(s => [s.id, s]));
    const order = Array.from(selected)
      .map(id => byId.get(id))
      .filter((s): s is Slide => !!s);

    setInsertingMsg(pickLoukMessage());
    try {
      const { hostUnavailable, inserted } = await insertManyIntoPresentation(order);
      if (hostUnavailable) {
        showToast('Insert not available — open in PowerPoint to use this feature');
        return;
      }
      Promise.all(order.slice(0, inserted).map(s =>
        api.trackInsertion(s.deckId, s.id).catch(() => undefined),
      ));
      showToast(`Inserted ${inserted}/${order.length} slides`);
      setSelected(new Set());
    } catch (e: any) {
      showToast(`Insert failed: ${e?.message || 'unknown'}`);
    } finally {
      setInsertingMsg(null);
    }
  }

  async function deleteSelected() {
    if (!slides) return;
    setBusyDelete(true);
    const ids = Array.from(selected);
    const failed: string[] = [];
    for (const id of ids) {
      try {
        await api.deleteSlide(deckId, id);
      } catch (e: any) {
        failed.push(id);
      }
    }
    setBusyDelete(false);
    setConfirmDeleteN(false);
    setSelected(new Set());
    if (failed.length === 0) {
      showToast(`Deleted ${ids.length} slide${ids.length === 1 ? '' : 's'}`);
    } else {
      showToast(`Deleted ${ids.length - failed.length}/${ids.length} — ${failed.length} failed`);
    }
    refetch();
  }

  async function saveTitle(slide: Slide, newTitle: string) {
    const trimmed = newTitle.trim();
    if (!trimmed || trimmed === slide.title) {
      setRenamingId(null);
      return;
    }
    try {
      const updated = await api.renameSlide(deckId, slide.id, trimmed);
      // Optimistic local merge so the UI updates without a full refetch
      if (slides) setData(slides.map(s => (s.id === slide.id ? updated : s)));
      showToast(`Renamed slide ${slide.position}`);
    } catch (e: any) {
      showToast(`Rename failed: ${e?.message || 'unknown'}`);
    } finally {
      setRenamingId(null);
    }
  }

  if (loading) return <div className="text-xs text-slate-500"><Loader2 size={12} className="inline animate-spin mr-1" /> Loading slides…</div>;
  if (error) return <div className="text-xs text-rose-600">Failed to load: {error.message} <button onClick={() => refetch()} className="underline ml-2">retry</button></div>;
  if (!slides || slides.length === 0) {
    return <div className="text-xs text-slate-500 italic">No slides for this deck yet. The worker may still be processing.</div>;
  }

  const selectedCount = selected.size;

  return (
    <div>
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <span className="text-[11px] text-slate-600">
          {selectedCount > 0 ? `${selectedCount} selected` : `${slides.length} slides`}
        </span>
        <button onClick={selectAll} className="text-[11px] px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded">
          Select all
        </button>
        <button onClick={clearSelection} disabled={selectedCount === 0} className="text-[11px] px-2 py-1 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-slate-700 rounded">
          Clear
        </button>
        <div className="flex-1" />
        {mode === 'office' && (
          <button
            onClick={insertSelected}
            disabled={selectedCount === 0 || insertingMsg !== null}
            className="text-[11px] px-3 py-1 bg-accent-500 hover:bg-accent-600 disabled:bg-slate-300 text-white rounded inline-flex items-center gap-1"
          >
            <Plus size={11} />
            {selectedCount > 0 ? `Insert ${selectedCount} selected` : 'Insert selected'}
          </button>
        )}
        <button
          onClick={() => setConfirmDeleteN(true)}
          disabled={selectedCount === 0 || busyDelete}
          className="text-[11px] px-3 py-1 bg-rose-50 hover:bg-rose-100 disabled:opacity-50 text-rose-700 rounded inline-flex items-center gap-1"
        >
          <Trash2 size={11} /> Delete{selectedCount > 0 ? ` ${selectedCount}` : ''}
        </button>
      </div>

      {insertingMsg && <LoukBanner message={insertingMsg} />}

      <div className="grid grid-cols-1 min-[300px]:grid-cols-2 min-[500px]:grid-cols-3 gap-2">

        {slides.map(s => {
          const isSelected = selected.has(s.id);
          const isRenaming = renamingId === s.id;
          return (
            <div
              key={s.id}
              className={`relative bg-white border rounded overflow-hidden ${isSelected ? 'border-accent-500 ring-2 ring-accent-200' : 'border-slate-200'}`}
            >
              <button
                onClick={() => toggle(s.id)}
                className={`absolute top-1 left-1 z-10 w-5 h-5 rounded border-2 flex items-center justify-center text-white text-[10px] ${
                  isSelected ? 'bg-accent-500 border-accent-500' : 'bg-white border-slate-300 hover:border-accent-400'
                }`}
                title={isSelected ? 'Deselect' : 'Select'}
              >
                {isSelected ? '✓' : ''}
              </button>
              <div className="aspect-[4/3] bg-slate-100 cursor-pointer" onClick={() => toggle(s.id)}>
                <img src={s.thumbnailBlobUrl} alt={`Slide ${s.position}`} className="w-full h-full object-contain" loading="lazy" />
              </div>
              <div className="p-1.5 text-[10px] text-slate-700">
                <span className="font-medium">{s.position}.</span>{' '}
                {isRenaming ? (
                  <input
                    autoFocus
                    defaultValue={s.title}
                    onBlur={e => saveTitle(s, e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                      if (e.key === 'Escape') setRenamingId(null);
                    }}
                    className="inline w-32 px-1 py-0 text-[10px] border border-slate-300 rounded"
                  />
                ) : (
                  <button
                    onClick={(e) => { e.stopPropagation(); setRenamingId(s.id); }}
                    className="text-left text-slate-700 hover:underline"
                    title="Click to rename"
                  >
                    {s.title || 'Untitled'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {confirmDeleteN && (
        <ConfirmDialog
          title={`Delete ${selectedCount} slide${selectedCount === 1 ? '' : 's'}?`}
          message={`This removes ${selectedCount} slide doc${selectedCount === 1 ? '' : 's'} + their thumbnail and pptx blobs. The source deck and other slides are untouched. Cannot be undone.`}
          confirmLabel="Delete"
          danger
          busy={busyDelete}
          onCancel={() => setConfirmDeleteN(false)}
          onConfirm={deleteSelected}
        />
      )}
    </div>
  );
}

function CuratorsTab({ showToast }: { showToast: (s: string) => void }) {
  const { data, refetch } = useApi<{ curators: Curator[]; pendingInvites: { email: string; role: Role }[] }>(() => api.listCurators());
  const [showInvite, setShowInvite] = useState(false);
  const [email, setEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('curator');
  const [busy, setBusy] = useState(false);
  const [savingRoleForId, setSavingRoleForId] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<Curator | null>(null);

  async function sendInvite() {
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.inviteCurator(email.trim(), inviteRole);
      showToast(`Invitation sent to ${email.trim()}`);
      setEmail('');
      setShowInvite(false);
      refetch();
    } catch (e: any) {
      showToast(`Invite failed: ${e?.message || 'unknown'}`);
    } finally {
      setBusy(false);
    }
  }

  async function changeRole(c: Curator, role: Role) {
    if (role === c.role) return;
    setSavingRoleForId(c.userId);
    try {
      await api.updateCuratorRole(c.userId, role);
      showToast(`${c.displayName || c.email} is now ${roleLabel(role)}`);
      refetch();
    } catch (e: any) {
      const msg = e instanceof ApiError ? `${e.status} ${e.message}` : e?.message || 'Role change failed';
      showToast(`Role change failed: ${msg}`);
    } finally {
      setSavingRoleForId(null);
    }
  }

  async function removeUser(c: Curator) {
    try {
      await api.removeCurator(c.userId);
      showToast(`Removed ${c.displayName || c.email}. They fall back to their AAD-group role on next login.`);
      setConfirmRemove(null);
      refetch();
    } catch (e: any) {
      showToast(`Remove failed: ${e?.message || 'unknown'}`);
    }
  }

  // Three-tier visual model: Admin (accent), Designer (emerald),
  // Viewer (slate / muted). Contributor + curator both render as Designer.
  const roleChipClass = (r: Role) =>
    r === 'admin'
      ? 'bg-accent-100 text-accent-800'
      : r === 'viewer'
        ? 'bg-slate-100 text-slate-600'
        : 'bg-emerald-100 text-emerald-800';

  return (
    <>
      <div className="flex justify-end mb-2">
        <button
          onClick={() => setShowInvite(true)}
          className="text-xs px-3 py-1.5 bg-accent-500 hover:bg-accent-600 text-white rounded inline-flex items-center gap-1"
        >
          <Mail size={12} /> Invite user
        </button>
      </div>

      {!data ? (
        <div className="text-sm text-slate-500 p-4">Loading users…</div>
      ) : (
        <>
          <ul className="space-y-1 mb-3">
            {data.curators.map(c => {
              // Fall back to derived role for docs that haven't been migrated
              // yet (shouldn't happen for new writes, but defensive).
              const effectiveRole: Role = c.role ?? (c.isPublisher ? 'curator' : 'contributor');
              return (
                <li key={c.id} className="bg-white border border-slate-200 rounded p-2.5 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-700">
                    {initials(c.displayName)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{c.displayName}</div>
                    <div className="text-[10px] text-slate-500 truncate">{c.email}{c.team ? ` · ${c.team}` : ''}</div>
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded ${roleChipClass(effectiveRole)}`}>
                    {roleLabel(effectiveRole)}
                  </span>
                  <select
                    // With contributor + curator collapsed to "Designer",
                    // we offer two options here. Selecting Designer writes
                    // role=curator (matches what the invite form does).
                    value={effectiveRole === 'admin' ? 'admin' : 'curator'}
                    onChange={e => changeRole(c, e.target.value as Role)}
                    disabled={savingRoleForId === c.userId}
                    className="text-[10px] px-1 py-0.5 border border-slate-200 rounded bg-white"
                    title="Change role"
                  >
                    <option value="curator">Designer</option>
                    <option value="admin">Admin</option>
                  </select>
                  <button
                    onClick={() => setConfirmRemove(c)}
                    className="p-1 text-rose-400 hover:text-rose-700"
                    title="Remove user (falls back to AAD-group role)"
                  >
                    <Trash2 size={12} />
                  </button>
                </li>
              );
            })}
          </ul>

          {data.pendingInvites.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Pending invites</div>
              <ul className="space-y-1">
                {data.pendingInvites.map(i => (
                  <li key={i.email} className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900 flex items-center gap-2">
                    <Mail size={12} /> {i.email}
                    <span className={`text-[10px] ml-auto px-1.5 py-0.5 rounded ${roleChipClass(i.role)}`}>{roleLabel(i.role)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {showInvite && (
        <Modal onClose={() => setShowInvite(false)} title="Invite a user">
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="email@example.com"
            className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-accent-400 mb-2"
          />
          <select
            value={inviteRole === 'admin' ? 'admin' : 'curator'}
            onChange={e => setInviteRole(e.target.value as Role)}
            className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded"
          >
            <option value="curator">Designer (upload, edit decks/slides, publish)</option>
            <option value="admin">Admin (everything + tenant settings)</option>
          </select>
          <p className="mt-2 text-[10px] text-slate-500">
            The invite is consumed at the user's next sign-in. All logins go through Microsoft SSO — no separate password.
          </p>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={() => setShowInvite(false)} className="text-xs px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded">
              Cancel
            </button>
            <button
              onClick={sendInvite}
              disabled={busy || !email.trim()}
              className="text-xs px-3 py-1.5 bg-accent-500 hover:bg-accent-600 disabled:bg-slate-300 text-white rounded"
            >
              {busy ? 'Sending…' : 'Send invite'}
            </button>
          </div>
        </Modal>
      )}

      {confirmRemove && (
        <ConfirmDialog
          title={`Remove ${confirmRemove.displayName || confirmRemove.email}?`}
          message={`Their per-user role override will be deleted. They keep access if they're a member of one of the mapped AAD role groups; otherwise they fall back to Contributor on their next sign-in.`}
          confirmLabel="Remove"
          danger
          onCancel={() => setConfirmRemove(null)}
          onConfirm={() => removeUser(confirmRemove)}
        />
      )}
    </>
  );
}

// ---------- Manage > Branding ----------
// Tenant-wide display config: app name shown in the header, tagline, accent
// color, and an optional logo. Persisted in the `branding` Cosmos doc keyed
// (`tenant`, tenantId). Bootstrap is responsible for the initial doc and the
// internal `adminGroupId` field — this surface only touches display fields.

const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const ACCEPTED_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml', 'image/webp'];

function BrandingTab({ showToast, onBrandingChange }: { showToast: (s: string) => void; onBrandingChange: () => void }) {
  const { data, loading, error, setData, refetch } = useApi<Branding>(() => api.getBranding());
  const [appName, setAppName] = useState('');
  const [tagline, setTagline] = useState('');
  const [accentColor, setAccentColor] = useState('#f97316');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Sync local form state once the fetch completes (or after a save).
  useEffect(() => {
    if (!data) return;
    setAppName(data.appName);
    setTagline(data.tagline);
    setAccentColor(data.accentColor || '#f97316');
    setDirty(false);
  }, [data]);

  if (loading) {
    return <div className="text-sm text-slate-500"><Loader2 size={12} className="inline animate-spin mr-1" /> Loading branding…</div>;
  }
  if (error) {
    return (
      <div className="text-sm text-rose-600">
        Failed to load branding: {error.message}
        <button onClick={() => refetch()} className="underline ml-2">retry</button>
      </div>
    );
  }
  if (!data) return null;

  const hexValid = /^#[0-9a-fA-F]{6}$/.test(accentColor);

  async function save() {
    if (!appName.trim() || !hexValid) return;
    setSaving(true);
    try {
      const next = await api.updateBranding({
        appName: appName.trim(),
        tagline: tagline.trim(),
        accentColor: accentColor.toLowerCase(),
      });
      setData(next);
      // Propagate to App.tsx so the header + global CSS vars re-apply.
      onBrandingChange();
      showToast('Branding saved');
    } catch (e: any) {
      const msg = e instanceof ApiError ? `${e.status} ${e.message}` : e?.message || 'Save failed';
      showToast(`Save failed: ${msg}`);
    } finally {
      setSaving(false);
    }
  }

  async function uploadLogo(file: File) {
    if (!ACCEPTED_LOGO_TYPES.includes(file.type)) {
      showToast(`Unsupported logo type — pick PNG, JPEG, SVG or WebP`);
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      showToast(`Logo exceeds 2 MB`);
      return;
    }
    setUploadingLogo(true);
    try {
      const next = await api.uploadBrandingLogo(file);
      setData(next);
      onBrandingChange();
      showToast('Logo updated');
    } catch (e: any) {
      const msg = e instanceof ApiError ? `${e.status} ${e.message}` : e?.message || 'Upload failed';
      showToast(`Logo upload failed: ${msg}`);
    } finally {
      setUploadingLogo(false);
    }
  }

  async function removeLogo() {
    setUploadingLogo(true);
    try {
      const next = await api.deleteBrandingLogo();
      setData(next);
      onBrandingChange();
      showToast('Logo removed');
    } catch (e: any) {
      showToast(`Remove failed: ${e?.message || 'unknown'}`);
    } finally {
      setUploadingLogo(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Live preview — uses local form state, not the saved doc, so admins
          see the brand they're editing before they commit. */}
      <div className="rounded border border-slate-200 overflow-hidden">
        <div className="text-[10px] uppercase tracking-wide text-slate-500 bg-slate-50 px-3 py-1.5 border-b border-slate-200">
          Header preview
        </div>
        <div
          className="px-4 py-3 flex items-center gap-3"
          style={{ background: `linear-gradient(135deg, ${accentColor}15, ${accentColor}30)` }}
        >
          {data.logoBlobUrl ? (
            <img
              src={data.logoBlobUrl}
              alt="logo"
              className="w-10 h-10 rounded object-contain bg-white border border-slate-200"
            />
          ) : (
            <div
              className="w-10 h-10 rounded flex items-center justify-center text-white font-bold text-sm"
              style={{ background: accentColor }}
            >
              {(appName || 'SL').slice(0, 2).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-800 truncate">
              {appName || <span className="text-slate-400 italic">App name…</span>}
            </div>
            <div className="text-[10px] text-slate-600 truncate">
              {tagline || <span className="text-slate-400 italic">Tagline appears here</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded p-4 space-y-3">
        <div>
          <label className="block text-[11px] font-medium text-slate-700 mb-1">App name</label>
          <input
            value={appName}
            onChange={e => { setAppName(e.target.value); setDirty(true); }}
            maxLength={80}
            placeholder="Slide Library"
            className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-accent-400"
          />
          <div className="text-[10px] text-slate-400 mt-0.5">Shown in the add-in header. Max 80 chars.</div>
        </div>

        <div>
          <label className="block text-[11px] font-medium text-slate-700 mb-1">Tagline</label>
          <input
            value={tagline}
            onChange={e => { setTagline(e.target.value); setDirty(true); }}
            maxLength={200}
            placeholder="Approved slides for the whole company"
            className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-accent-400"
          />
        </div>

        <div>
          <label className="block text-[11px] font-medium text-slate-700 mb-1 flex items-center gap-1">
            <Palette size={11} /> Accent color
          </label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={hexValid ? accentColor : '#f97316'}
              onChange={e => { setAccentColor(e.target.value.toLowerCase()); setDirty(true); }}
              className="w-9 h-8 rounded border border-slate-200 cursor-pointer"
              title="Pick color"
            />
            <input
              type="text"
              value={accentColor}
              onChange={e => { setAccentColor(e.target.value); setDirty(true); }}
              placeholder="#f97316"
              className={`w-28 px-2 py-1.5 text-xs font-mono border rounded focus:outline-none ${
                hexValid ? 'border-slate-200 focus:border-accent-400' : 'border-rose-400'
              }`}
            />
            {!hexValid && <span className="text-[10px] text-rose-600">Expected #rrggbb</span>}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          {data.updatedAt && (
            <span className="text-[10px] text-slate-400 mr-auto">
              Last updated {new Date(data.updatedAt).toLocaleString()} by {data.updatedBy}
            </span>
          )}
          <button
            onClick={save}
            disabled={!dirty || saving || !appName.trim() || !hexValid}
            className="text-xs px-3 py-1.5 bg-accent-500 hover:bg-accent-600 disabled:bg-slate-300 text-white rounded inline-flex items-center gap-1"
          >
            <Check size={12} /> {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded p-4 space-y-2">
        <div className="text-[11px] font-medium text-slate-700 flex items-center gap-1">
          <ImageIcon size={11} /> Logo
        </div>
        <div className="flex items-center gap-3">
          {data.logoBlobUrl ? (
            <img
              src={data.logoBlobUrl}
              alt="Current logo"
              className="w-16 h-16 rounded object-contain bg-slate-50 border border-slate-200"
            />
          ) : (
            <div className="w-16 h-16 rounded bg-slate-50 border border-dashed border-slate-300 flex items-center justify-center text-[10px] text-slate-400">
              No logo
            </div>
          )}
          <div className="flex-1 text-[10px] text-slate-500">
            PNG / JPEG / SVG / WebP, max 2 MB. Square images render best.
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded inline-flex items-center gap-1 cursor-pointer">
              <Upload size={11} /> {uploadingLogo ? 'Working…' : data.logoBlobUrl ? 'Replace' : 'Upload'}
              <input
                type="file"
                accept={ACCEPTED_LOGO_TYPES.join(',')}
                className="hidden"
                disabled={uploadingLogo}
                onChange={e => {
                  const f = e.target.files?.[0];
                  if (f) uploadLogo(f);
                  e.target.value = '';
                }}
              />
            </label>
            {data.logoBlobUrl && (
              <button
                onClick={removeLogo}
                disabled={uploadingLogo}
                className="text-[11px] px-2 py-1 bg-rose-50 hover:bg-rose-100 disabled:opacity-50 text-rose-700 rounded inline-flex items-center gap-1"
              >
                <Trash2 size={11} /> Remove
              </button>
            )}
          </div>
        </div>
      </div>

      <RoleGroupMappingCard
        branding={data}
        onUpdated={(next) => setData(next)}
        showToast={showToast}
      />

      <NotificationChannelsCard
        branding={data}
        onUpdated={(next) => setData(next)}
        showToast={showToast}
      />

      <NotificationSenderCard
        branding={data}
        onUpdated={(next) => setData(next)}
        showToast={showToast}
      />

      <TeamsFallbackChannelCard
        current={data.teamsFallbackChannel}
        onUpdated={(channel) => setData({ ...data, teamsFallbackChannel: channel ?? undefined })}
        showToast={showToast}
      />
    </div>
  );
}

// ---------- Branding > Role group mapping ----------
// Maps roles to AAD security groups. With the role collapse (PR #60), the
// product surfaces only two roles: Admin (settings + everything) and
// Designer (upload + edit + publish). Backend still stores three group slots
// (`adminsGroupId`, `curatorsGroupId`, `contributorsGroupId`) for the legacy
// 3-role schema, but the UI maps:
//   - Admins row  → `adminsGroupId`
//   - Designers row → `curatorsGroupId ?? contributorsGroupId` for display.
//     Save writes the picked GUID to `curatorsGroupId` only and leaves
//     `contributorsGroupId` untouched, so any existing tenant that mapped a
//     "Contributors" group keeps working (members still resolve to Designer
//     via the curator>contributor backend ordering). Clearing the row nulls
//     both slots.

function RoleGroupMappingCard({
  branding, onUpdated, showToast,
}: {
  branding: Branding;
  onUpdated: (next: Branding) => void;
  showToast: (s: string) => void;
}) {
  // Per-row controlled state. null = unset. Designers row prefers the
  // curators slot for display; falls back to contributors slot for tenants
  // that mapped a group there before the role collapse.
  const [admins, setAdmins] = useState<string | null>(branding.adminsGroupId ?? null);
  const [designers, setDesigners] = useState<string | null>(
    branding.curatorsGroupId ?? branding.contributorsGroupId ?? null,
  );
  const [saving, setSaving] = useState(false);
  // Cache of GUID → displayName for the currently-selected groups so admins
  // see "Marketing Ops" instead of a raw GUID. Filled lazily on mount from
  // /groups/lookup and updated by the picker on every pick.
  const [nameCache, setNameCache] = useState<Map<string, string>>(new Map());
  // Which row's picker is open (null = closed). The picker is a single
  // shared modal; on selection it writes via the row's setter.
  const [picker, setPicker] = useState<null | { role: 'admins' | 'designers'; current: string | null }>(null);

  // Re-sync when the parent's branding doc changes (e.g. after Save).
  useEffect(() => {
    setAdmins(branding.adminsGroupId ?? null);
    setDesigners(branding.curatorsGroupId ?? branding.contributorsGroupId ?? null);
  }, [branding.adminsGroupId, branding.curatorsGroupId, branding.contributorsGroupId]);

  // Resolve display names for whatever GUIDs are currently in the doc.
  useEffect(() => {
    const ids = [branding.adminsGroupId, branding.curatorsGroupId, branding.contributorsGroupId]
      .filter((s): s is string => !!s);
    if (ids.length === 0) return;
    api.lookupGroups(ids)
      .then(r => {
        setNameCache(prev => {
          const next = new Map(prev);
          for (const g of r.groups) next.set(g.id, g.displayName);
          return next;
        });
      })
      .catch(() => undefined);
  }, [branding.adminsGroupId, branding.curatorsGroupId, branding.contributorsGroupId]);

  // The "current" designers value for dirty-check purposes is whichever
  // slot is populated — same precedence as the initial state.
  const currentDesigners = branding.curatorsGroupId ?? branding.contributorsGroupId ?? null;
  const dirty =
    (admins ?? null) !== (branding.adminsGroupId ?? null) ||
    (designers ?? null) !== currentDesigners;

  async function save() {
    setSaving(true);
    try {
      // When the admin clears the Designers row, null BOTH backend slots so
      // legacy contributors-group memberships also stop granting Designer.
      // When setting a value, write to the curators slot only and leave the
      // contributors slot alone (preserves an existing mapping during the
      // transition; backend treats either as Designer-equivalent).
      // Use the inline updateBranding signature (string | null) — the
      // Branding type itself has these as `string?` so a Partial<Branding>
      // wouldn't accept the explicit null we need to clear the slot.
      const payload: Parameters<typeof api.updateBranding>[0] = { adminsGroupId: admins };
      if (designers === null) {
        payload.curatorsGroupId = null;
        payload.contributorsGroupId = null;
      } else {
        payload.curatorsGroupId = designers;
      }
      const next = await api.updateBranding(payload);
      onUpdated(next);
      showToast('Role group mapping saved');
    } catch (e: any) {
      const msg = e instanceof ApiError ? `${e.status} ${e.message}` : e?.message || 'Save failed';
      showToast(`Save failed: ${msg}`);
    } finally {
      setSaving(false);
    }
  }

  // When the picker returns a group, write it into the right row + cache
  // the name so the chip can render without a second Graph call.
  function handlePick(group: GraphGroup) {
    if (!picker) return;
    setNameCache(prev => new Map(prev).set(group.id, group.displayName));
    if (picker.role === 'admins') setAdmins(group.id);
    else setDesigners(group.id);
    setPicker(null);
  }

  const rowOpts = (role: 'admins' | 'designers', value: string | null, setValue: (v: string | null) => void) => ({
    value,
    displayName: value ? nameCache.get(value) : undefined,
    onChange: setValue,
    onPick: () => setPicker({ role, current: value }),
    onClear: () => setValue(null),
  });

  return (
    <div className="bg-white border border-slate-200 rounded p-4 space-y-2">
      <div className="text-[11px] font-medium text-slate-700 flex items-center gap-1">
        <Users size={11} /> Role group mapping
      </div>
      <p className="text-[10px] text-slate-500">
        Map each role to an AAD security group. Members of those groups are auto-granted the role at login.
        Per-user invites in <b>Manage → Curators</b> still work alongside this and take precedence when both apply.
        Leave a field empty to skip the AAD-group path for that role.
      </p>
      <RoleGroupField label="Admins" {...rowOpts('admins', admins, setAdmins)} />
      <RoleGroupField label="Designers" {...rowOpts('designers', designers, setDesigners)} />
      <div className="flex justify-end pt-1">
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="text-xs px-3 py-1.5 bg-accent-500 hover:bg-accent-600 disabled:bg-slate-300 text-white rounded inline-flex items-center gap-1"
        >
          {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={12} />} Save
        </button>
      </div>
      {picker && (
        <SingleGroupPickerModal
          title={`Pick an AAD group — ${picker.role}`}
          onPick={handlePick}
          onClose={() => setPicker(null)}
          showToast={showToast}
        />
      )}
    </div>
  );
}

function RoleGroupField({
  label, value, displayName, onChange, onPick, onClear,
}: {
  label: string;
  value: string | null;
  /** Resolved display name for `value`, when known. Defaults to the GUID. */
  displayName?: string;
  onChange: (next: string | null) => void;
  onPick: () => void;
  onClear: () => void;
}) {
  // Two rendering modes:
  //   - When a group is set: friendly chip with display name + GUID hint.
  //     Reduces accidental typo edits and matches the deck restrictions UX.
  //   - When unset: empty input + Pick button to launch the modal.
  return (
    <div className="flex items-center gap-2 py-0.5">
      <label className="text-[11px] text-slate-700 w-24 flex-shrink-0">{label}</label>
      {value ? (
        <div className="flex-1 flex items-center gap-1 px-2 py-1 text-xs border border-slate-200 rounded bg-slate-50">
          <span className="font-medium text-slate-800 truncate">{displayName ?? 'Resolving…'}</span>
          <span className="text-[10px] text-slate-400 font-mono truncate" title={value}>{value}</span>
        </div>
      ) : (
        <input
          type="text"
          value=""
          onChange={e => {
            const v = e.target.value.trim();
            // Power-user escape hatch: pasting a GUID-shaped value writes
            // directly without opening the modal.
            if (v && /^[0-9a-fA-F-]{32,}$/.test(v)) onChange(v);
          }}
          placeholder="Click Pick to search, or paste a GUID"
          className="flex-1 px-2 py-1 text-xs font-mono border border-slate-200 rounded focus:outline-none focus:border-accent-400"
        />
      )}
      <button
        type="button"
        onClick={onPick}
        className="text-[11px] px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded inline-flex items-center gap-1"
        title="Search AAD groups"
      >
        <Search size={11} /> Pick
      </button>
      <button
        type="button"
        onClick={onClear}
        disabled={!value}
        className="p-1 text-rose-400 hover:text-rose-700 disabled:opacity-30"
        title="Clear"
      >
        <X size={12} />
      </button>
    </div>
  );
}

// Search-as-you-type modal that returns ONE picked group via onPick. Reuses
// the existing /groups/search endpoint behind a debounce. Sibling to the
// per-deck multi-select GroupPickerModal — same Graph search backend, but
// single-pick semantics so the role-group fields can use it without a
// multi-select UI.
function SingleGroupPickerModal({
  title, onPick, onClose, showToast,
}: {
  title: string;
  onPick: (group: GraphGroup) => void;
  onClose: () => void;
  showToast: (s: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GraphGroup[] | null>(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.searchGroups(query);
        if (!cancelled) setResults(r.groups);
      } catch (e: any) {
        if (!cancelled) {
          showToast(`Group search failed: ${e?.message || 'unknown'}`);
          setResults([]);
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, showToast]);

  return (
    <Modal title={title} onClose={onClose}>
      <div className="space-y-2">
        <div className="relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search AAD groups by name…"
            className="w-full pl-7 pr-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-accent-400"
          />
        </div>
        <div className="max-h-72 overflow-y-auto border border-slate-200 rounded bg-slate-50">
          {searching && !results ? (
            <div className="text-[11px] text-slate-500 p-2 inline-flex items-center gap-1">
              <Loader2 size={11} className="animate-spin" /> Searching…
            </div>
          ) : !results || results.length === 0 ? (
            <div className="text-[11px] text-slate-400 italic p-2">
              {query ? 'No groups match.' : 'Your groups will appear here. Start typing to search the directory.'}
            </div>
          ) : (
            <ul className="divide-y divide-slate-200">
              {results.map(g => (
                <li key={g.id}>
                  <button
                    onClick={() => onPick(g)}
                    className="w-full text-left px-3 py-2 hover:bg-white"
                  >
                    <div className="text-xs text-slate-800 truncate">{g.displayName}</div>
                    <div className="text-[10px] text-slate-500 truncate">{g.id}</div>
                    {g.description && (
                      <div className="text-[10px] text-slate-500 truncate">{g.description}</div>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="flex justify-end">
          <button onClick={onClose} className="text-xs px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded">Cancel</button>
        </div>
      </div>
    </Modal>
  );
}

// ---------- Branding > Notification channels (admin kill-switch) ----------
// Tenant-wide on/off per channel. When admin turns off email here, nobody
// in the tenant gets email notifications, regardless of their per-user
// pref. In-app and Teams behave the same way independently.
//
// Test sends from Manage > Notifications bypass these toggles so admins can
// keep validating templates while a channel is silenced.

function NotificationChannelsCard({
  branding, onUpdated, showToast,
}: {
  branding: Branding;
  onUpdated: (next: Branding) => void;
  showToast: (s: string) => void;
}) {
  // Resolve current state (undefined means on by default).
  const initial = (b: Branding) => ({
    inApp: b.notificationChannels?.inApp !== false,
    email: b.notificationChannels?.email !== false,
    teams: b.notificationChannels?.teams !== false,
  });
  const [state, setState] = useState(initial(branding));
  const [busy, setBusy] = useState<'inApp' | 'email' | 'teams' | null>(null);

  useEffect(() => { setState(initial(branding)); }, [branding]);

  async function toggle(channel: 'inApp' | 'email' | 'teams') {
    const next = { ...state, [channel]: !state[channel] };
    setState(next); // optimistic
    setBusy(channel);
    try {
      const updated = await api.updateBranding({
        notificationChannels: {
          inApp: next.inApp, email: next.email, teams: next.teams,
        },
      });
      onUpdated(updated);
      const label = channel === 'inApp' ? 'In-app' : channel === 'email' ? 'Email' : 'Teams';
      showToast(`${label} notifications ${next[channel] ? 'enabled' : 'disabled'} tenant-wide`);
    } catch (e: any) {
      // Revert optimistic flip.
      setState(initial(branding));
      const msg = e instanceof ApiError ? `${e.status} ${e.message}` : e?.message || 'Update failed';
      showToast(`Toggle failed: ${msg}`);
    } finally {
      setBusy(null);
    }
  }

  const allOn = state.inApp && state.email && state.teams;

  return (
    <div className="bg-white border border-slate-200 rounded p-4 space-y-2">
      <div className="text-[11px] font-medium text-slate-700 flex items-center gap-1">
        <Bell size={11} /> Notification channels
      </div>
      <p className="text-[10px] text-slate-500">
        Tenant-wide kill-switch per channel. Turning a channel off silences it for everyone, regardless of their personal preference.
        Test sends from <b>Manage → Notifications</b> bypass these toggles so you can still verify templates.
      </p>

      <ToggleRow
        label="In-app inbox"
        hint="The bell-icon dropdown. Off = nothing written to the notifications inbox; user badge stays at 0."
        checked={state.inApp}
        busy={busy === 'inApp'}
        onToggle={() => toggle('inApp')}
      />
      <ToggleRow
        label="Email"
        hint="Workflow-event emails. Off = no mail sent, regardless of per-user pref or Mail.Send configuration."
        checked={state.email}
        busy={busy === 'email'}
        onToggle={() => toggle('email')}
      />
      <ToggleRow
        label="Microsoft Teams"
        hint="Direct chat + channel fallback. Off = no Graph chat calls fire."
        checked={state.teams}
        busy={busy === 'teams'}
        onToggle={() => toggle('teams')}
      />

      {!allOn && (
        <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-2">
          One or more channels are off tenant-wide. Submit / publish / reject still succeed; the silenced channel(s) just won't deliver.
        </div>
      )}
    </div>
  );
}

// ---------- Branding > Notification sender mailbox ----------
// UPN of the service mailbox notification emails are sent from. When unset
// (default), emails are sent FROM the user who triggered the workflow event
// via OBO. When set, the backend uses app-only Mail.Send to route through
// that mailbox — looks more polished, doesn't clutter user Sent folders.

function NotificationSenderCard({
  branding, onUpdated, showToast,
}: {
  branding: Branding;
  onUpdated: (next: Branding) => void;
  showToast: (s: string) => void;
}) {
  const [upn, setUpn] = useState<string>(branding.notificationSenderUpn ?? '');
  const [saving, setSaving] = useState(false);

  // Re-sync if the parent's branding changes (e.g. after a different save).
  useEffect(() => {
    setUpn(branding.notificationSenderUpn ?? '');
  }, [branding.notificationSenderUpn]);

  const dirty = upn.trim() !== (branding.notificationSenderUpn ?? '');
  const looksLikeUpn = !upn.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(upn.trim());

  async function save() {
    setSaving(true);
    try {
      const next = await api.updateBranding({
        notificationSenderUpn: upn.trim() ? upn.trim() : null,
      });
      onUpdated(next);
      showToast(upn.trim() ? `Notification sender set to ${upn.trim()}` : 'Notification sender cleared — emails revert to the acting user');
    } catch (e: any) {
      const msg = e instanceof ApiError ? `${e.status} ${e.message}` : e?.message || 'Save failed';
      showToast(`Save failed: ${msg}`);
    } finally {
      setSaving(false);
    }
  }

  async function clear() {
    setUpn('');
    setSaving(true);
    try {
      const next = await api.updateBranding({ notificationSenderUpn: null });
      onUpdated(next);
      showToast('Notification sender cleared');
    } catch (e: any) {
      showToast(`Clear failed: ${e?.message || 'unknown'}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-white border border-slate-200 rounded p-4 space-y-2">
      <div className="text-[11px] font-medium text-slate-700 flex items-center gap-1">
        <Mail size={11} /> Notification sender mailbox
      </div>
      <p className="text-[10px] text-slate-500">
        UPN of the service mailbox that workflow-event emails should be sent from.
        Leave empty to send from the user who triggered the action.
      </p>
      <div className="flex items-center gap-2 pt-1">
        <input
          type="text"
          value={upn}
          onChange={e => setUpn(e.target.value)}
          placeholder="slidelib-notifications@tenant.com"
          className={`flex-1 px-2 py-1.5 text-sm font-mono border rounded focus:outline-none ${
            looksLikeUpn ? 'border-slate-200 focus:border-accent-400' : 'border-rose-400'
          }`}
        />
        <button
          onClick={save}
          disabled={!dirty || saving || !looksLikeUpn}
          className="text-xs px-3 py-1.5 bg-accent-500 hover:bg-accent-600 disabled:bg-slate-300 text-white rounded inline-flex items-center gap-1"
        >
          {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={12} />} Save
        </button>
        {branding.notificationSenderUpn && (
          <button
            onClick={clear}
            disabled={saving}
            className="text-xs px-3 py-1.5 bg-rose-50 hover:bg-rose-100 disabled:opacity-50 text-rose-700 rounded inline-flex items-center gap-1"
            title="Revert to actor sends"
          >
            <Trash2 size={11} /> Clear
          </button>
        )}
      </div>
      {!looksLikeUpn && <div className="text-[10px] text-rose-600">Doesn't look like a UPN. Use the email@domain.tld shape.</div>}
      <details className="text-[10px] text-slate-500 mt-2">
        <summary className="cursor-pointer hover:text-slate-700">Required Entra setup (one-time)</summary>
        <ol className="list-decimal ml-4 mt-1 space-y-1">
          <li>In Entra, grant <code className="font-mono">Mail.Send</code> <b>application</b> permission (not delegated) on the Slide Library app + admin-consent.</li>
          <li>Provision the service mailbox (any licensed Exchange Online mailbox works, e.g. slidelib-notifications@yourdomain.com).</li>
          <li>Restrict the app to only this mailbox via Exchange Online's <code className="font-mono">New-ApplicationAccessPolicy</code> so the app can't send as any other user. See the PR description for the cmdlets.</li>
        </ol>
        <p className="mt-1 italic">
          Without step 3 the app could send as any mailbox in the tenant. The app falls back to actor-OBO if the app-only path fails — your notifications still go out, just from the acting user.
        </p>
      </details>
    </div>
  );
}

// ---------- Teams fallback channel picker ----------
// Sits inside the Branding tab. Lets an admin pick which Teams channel
// receives notifications when a 1:1 DM fails (e.g. recipient has Teams
// disabled). Two-step picker: pick a Team, then pick a channel.

function TeamsFallbackChannelCard({
  current,
  onUpdated,
  showToast,
}: {
  current: Branding['teamsFallbackChannel'];
  onUpdated: (next: Branding['teamsFallbackChannel'] | null) => void;
  showToast: (s: string) => void;
}) {
  const [picking, setPicking] = useState(false);
  const [saving, setSaving] = useState(false);

  async function clear() {
    setSaving(true);
    try {
      await api.updateBranding({ teamsFallbackChannel: null });
      onUpdated(null);
      showToast('Teams fallback channel cleared');
    } catch (e: any) {
      showToast(`Clear failed: ${e?.message || 'unknown'}`);
    } finally {
      setSaving(false);
    }
  }

  async function save(team: TeamRef, channel: ChannelRef) {
    setSaving(true);
    try {
      const next = {
        teamId: team.id,
        teamName: team.displayName,
        channelId: channel.id,
        channelName: channel.displayName,
      };
      await api.updateBranding({ teamsFallbackChannel: next });
      onUpdated(next);
      setPicking(false);
      showToast(`Set Teams fallback to ${team.displayName} → ${channel.displayName}`);
    } catch (e: any) {
      const msg = e instanceof ApiError ? `${e.status} ${e.message}` : e?.message || 'Save failed';
      showToast(`Save failed: ${msg}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-white border border-slate-200 rounded p-4 space-y-2">
      <div className="text-[11px] font-medium text-slate-700 flex items-center gap-1">
        <Send size={11} /> Teams fallback channel
      </div>
      <div className="text-[10px] text-slate-500">
        Used when a 1:1 Teams DM can't be delivered (recipient has Teams disabled, etc).
        Leave unset to skip the fallback entirely.
      </div>
      <div className="flex items-center gap-3 pt-1">
        {current ? (
          <div className="flex-1 text-xs">
            <div className="font-medium text-slate-800">{current.teamName}</div>
            <div className="text-[10px] text-slate-500"># {current.channelName}</div>
          </div>
        ) : (
          <div className="flex-1 text-[11px] text-slate-400 italic">No fallback channel set.</div>
        )}
        <div className="flex flex-col gap-1 flex-shrink-0">
          <button
            onClick={() => setPicking(true)}
            disabled={saving}
            className="text-[11px] px-2 py-1 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-slate-700 rounded inline-flex items-center gap-1"
          >
            {current ? 'Change' : 'Pick channel'}
          </button>
          {current && (
            <button
              onClick={clear}
              disabled={saving}
              className="text-[11px] px-2 py-1 bg-rose-50 hover:bg-rose-100 disabled:opacity-50 text-rose-700 rounded inline-flex items-center gap-1"
            >
              <Trash2 size={11} /> Clear
            </button>
          )}
        </div>
      </div>
      {picking && <TeamsChannelPickerModal onCancel={() => setPicking(false)} onPick={save} />}
    </div>
  );
}

function TeamsChannelPickerModal({
  onCancel,
  onPick,
}: {
  onCancel: () => void;
  onPick: (team: TeamRef, channel: ChannelRef) => void;
}) {
  const [teams, setTeams] = useState<TeamRef[] | null>(null);
  const [teamsError, setTeamsError] = useState<string | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<TeamRef | null>(null);
  const [channels, setChannels] = useState<ChannelRef[] | null>(null);
  const [channelsError, setChannelsError] = useState<string | null>(null);

  useEffect(() => {
    api.listJoinedTeams()
      .then(r => setTeams(r.teams))
      .catch(e => setTeamsError(e?.message || 'Failed to load teams'));
  }, []);

  useEffect(() => {
    if (!selectedTeam) return;
    setChannels(null);
    setChannelsError(null);
    api.listTeamChannels(selectedTeam.id)
      .then(r => setChannels(r.channels))
      .catch(e => setChannelsError(e?.message || 'Failed to load channels'));
  }, [selectedTeam]);

  return (
    <Modal title={selectedTeam ? `Channels in ${selectedTeam.displayName}` : 'Pick a Team'} onClose={onCancel}>
      {!selectedTeam ? (
        <div>
          {teamsError ? (
            <div className="text-xs text-rose-600">{teamsError}</div>
          ) : !teams ? (
            <div className="text-xs text-slate-500 inline-flex items-center gap-1">
              <Loader2 size={11} className="animate-spin" /> Loading your Teams…
            </div>
          ) : teams.length === 0 ? (
            <div className="text-xs text-slate-500 italic">You're not in any Teams.</div>
          ) : (
            <ul className="max-h-72 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded">
              {teams.map(t => (
                <li key={t.id}>
                  <button onClick={() => setSelectedTeam(t)} className="w-full text-left px-3 py-2 hover:bg-slate-50">
                    <div className="text-sm text-slate-800">{t.displayName}</div>
                    {t.description && <div className="text-[10px] text-slate-500 truncate">{t.description}</div>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <div>
          <button onClick={() => setSelectedTeam(null)} className="text-[11px] text-slate-600 hover:underline mb-2">
            ← Back to Teams
          </button>
          {channelsError ? (
            <div className="text-xs text-rose-600">{channelsError}</div>
          ) : !channels ? (
            <div className="text-xs text-slate-500 inline-flex items-center gap-1">
              <Loader2 size={11} className="animate-spin" /> Loading channels…
            </div>
          ) : channels.length === 0 ? (
            <div className="text-xs text-slate-500 italic">No channels in this team.</div>
          ) : (
            <ul className="max-h-72 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded">
              {channels.map(c => (
                <li key={c.id}>
                  <button onClick={() => onPick(selectedTeam, c)} className="w-full text-left px-3 py-2 hover:bg-slate-50">
                    <div className="text-sm text-slate-800"># {c.displayName}</div>
                    {c.description && <div className="text-[10px] text-slate-500 truncate">{c.description}</div>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <div className="flex justify-end pt-3">
        <button onClick={onCancel} className="text-xs px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded">Cancel</button>
      </div>
    </Modal>
  );
}

// ---------- Analytics ----------

function AnalyticsView() {
  const { data, loading, error } = useApi<{ totals: any; byDeck: any[]; since: string }>(() => api.analytics());

  if (loading) return <div className="p-6 text-sm text-slate-500">Loading analytics…</div>;
  if (error) return <div className="p-6 text-sm text-red-600">Failed to load analytics: {error.message}</div>;
  if (!data) return null;

  const totals = data.totals || {};
  return (
    <div className="p-4 space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <StatCard label="Insertions" value={totals.insertions ?? 0} icon={<BarChart3 size={14} />} />
        <StatCard label="Active users" value={totals.users ?? 0} icon={<Users size={14} />} />
        <StatCard label="Published decks" value={totals.decks ?? 0} icon={<FileText size={14} />} />
        <StatCard label="Avg / user" value={totals.avgPerUser ?? 0} icon={<BarChart3 size={14} />} />
      </div>

      <div>
        <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-1">Top decks</div>
        {data.byDeck.length === 0 ? (
          <div className="text-sm text-slate-500 p-4 bg-white rounded border border-slate-200">No insertion events yet.</div>
        ) : (
          <ul className="space-y-1">
            {data.byDeck.slice(0, 10).map((d, idx) => (
              <li key={idx} className="bg-white border border-slate-200 rounded p-2.5 flex items-center justify-between text-xs">
                <span className="truncate">{d.title || d.deckId}</span>
                <span className="text-slate-500 font-mono">{d.insertions}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="text-[10px] text-slate-400">Showing data since {new Date(data.since).toLocaleDateString()}.</div>
    </div>
  );
}

function StatCard({ label, value, icon }: { label: string; value: number | string; icon: React.ReactNode }) {
  return (
    <div className="bg-white border border-slate-200 rounded p-3">
      <div className="flex items-center justify-between text-slate-500 text-[10px] uppercase tracking-wide mb-1">
        <span>{label}</span>{icon}
      </div>
      <div className="text-xl font-semibold text-slate-800">{value}</div>
    </div>
  );
}

// ---------- Manage > Categories panel ----------
// Admin-only editor for the tenant taxonomy. Local edit buffer so cancel
// works; Save runs PUT /categories and pushes the new tree up via
// onSaved() so other tabs see the change immediately.

function CategoriesPanel({
  tree, onSaved, showToast,
}: {
  tree: CategoryTree | null;
  onSaved: () => void;
  showToast: (s: string) => void;
}) {
  const [draft, setDraft] = useState<CategoryNode[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pull tree into local edit buffer once it loads.
  useEffect(() => {
    if (tree && !draft) setDraft(deepCloneCategories(tree.categories));
  }, [tree, draft]);

  if (!tree || !draft) {
    return (
      <div className="text-sm text-slate-500 inline-flex items-center gap-1">
        <Loader2 size={12} className="animate-spin" /> Loading categories…
      </div>
    );
  }

  // All mutation helpers operate on the local draft; nothing hits the
  // network until Save. Slugs auto-derive from the name on add; renaming
  // does NOT change the slug — that's the whole point of stable IDs.
  // Each helper takes `current` (already narrowed non-null via the guard
  // above) so TS can follow.
  function addCategory(current: CategoryNode[]) {
    const id = nextSlug('cat', current.map(c => c.id));
    setDraft([...current, { id, name: 'New category', sortOrder: current.length, subCategories: [] }]);
  }
  function renameCategory(current: CategoryNode[], idx: number, name: string) {
    const next = [...current];
    next[idx] = { ...next[idx], name };
    setDraft(next);
  }
  function moveCategory(current: CategoryNode[], idx: number, dir: -1 | 1) {
    const j = idx + dir;
    if (j < 0 || j >= current.length) return;
    const next = [...current];
    [next[idx], next[j]] = [next[j], next[idx]];
    setDraft(next.map((c, i) => ({ ...c, sortOrder: i })));
  }
  function deleteCategory(current: CategoryNode[], idx: number) {
    setDraft(current.filter((_, i) => i !== idx).map((c, i) => ({ ...c, sortOrder: i })));
  }
  function addSub(current: CategoryNode[], catIdx: number) {
    const next = [...current];
    const c = next[catIdx];
    const id = nextSlug('sub', c.subCategories.map(s => s.id));
    next[catIdx] = {
      ...c,
      subCategories: [...c.subCategories, { id, name: 'New sub-category', sortOrder: c.subCategories.length }],
    };
    setDraft(next);
  }
  function renameSub(current: CategoryNode[], catIdx: number, subIdx: number, name: string) {
    const next = [...current];
    const subs = [...next[catIdx].subCategories];
    subs[subIdx] = { ...subs[subIdx], name };
    next[catIdx] = { ...next[catIdx], subCategories: subs };
    setDraft(next);
  }
  function moveSub(current: CategoryNode[], catIdx: number, subIdx: number, dir: -1 | 1) {
    const subs = [...current[catIdx].subCategories];
    const j = subIdx + dir;
    if (j < 0 || j >= subs.length) return;
    [subs[subIdx], subs[j]] = [subs[j], subs[subIdx]];
    const next = [...current];
    next[catIdx] = { ...next[catIdx], subCategories: subs.map((s, i) => ({ ...s, sortOrder: i })) };
    setDraft(next);
  }
  function deleteSub(current: CategoryNode[], catIdx: number, subIdx: number) {
    const next = [...current];
    next[catIdx] = {
      ...next[catIdx],
      subCategories: next[catIdx].subCategories.filter((_, i) => i !== subIdx).map((s, i) => ({ ...s, sortOrder: i })),
    };
    setDraft(next);
  }

  async function save() {
    if (!draft) return;
    setSaving(true);
    setError(null);
    try {
      await api.updateCategories(draft);
      showToast('Categories saved');
      onSaved();
      setDraft(null); // re-pulls from updated tree
    } catch (e: any) {
      // 409 from the backend means decks still reference removed cats/subs.
      const body = (e as ApiError)?.body;
      if (body?.orphanedReferences?.length) {
        const list = body.orphanedReferences
          .map((r: any) => `${r.category}${r.subCategory ? `/${r.subCategory}` : ''} (${r.count} deck${r.count === 1 ? '' : 's'})`)
          .join(', ');
        setError(`Some categories are still in use — re-assign the affected decks first: ${list}`);
      } else {
        setError(e instanceof ApiError ? `${e.status} ${e.message}` : (e?.message || 'Save failed'));
      }
    } finally {
      setSaving(false);
    }
  }

  const dirty = !isCategoriesEqual(draft, tree.categories);

  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-600">
        Manage the taxonomy used by Browse filters and the New Draft form. Slugs are stable IDs — renaming a
        category here doesn't break existing decks. Removing a category still in use by a deck is blocked.
      </div>

      <div className="space-y-2">
        {draft.map((c, i) => (
          <div key={c.id} className="bg-white border border-slate-200 rounded p-2.5 space-y-2">
            <div className="flex items-center gap-1.5">
              <GripVertical size={14} className="text-slate-300 flex-shrink-0" />
              <input
                value={c.name}
                onChange={e => renameCategory(draft, i, e.target.value)}
                maxLength={60}
                className="flex-1 px-2 py-1 text-sm font-medium border border-transparent hover:border-slate-200 focus:border-accent-400 focus:outline-none rounded"
              />
              <span className="text-[10px] text-slate-400 font-mono">{c.id}</span>
              <button onClick={() => moveCategory(draft, i, -1)} disabled={i === 0} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Move up">
                <ChevronUp size={12} />
              </button>
              <button onClick={() => moveCategory(draft, i, 1)} disabled={i === draft.length - 1} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Move down">
                <ChevronDown size={12} />
              </button>
              <button onClick={() => deleteCategory(draft, i)} className="p-1 text-rose-400 hover:text-rose-700" title="Delete category">
                <Trash2 size={12} />
              </button>
            </div>
            <div className="pl-6 space-y-1">
              {c.subCategories.map((s, j) => (
                <div key={s.id} className="flex items-center gap-1.5">
                  <span className="text-slate-300 text-[10px]">↳</span>
                  <input
                    value={s.name}
                    onChange={e => renameSub(draft, i, j, e.target.value)}
                    maxLength={60}
                    className="flex-1 px-2 py-0.5 text-xs border border-transparent hover:border-slate-200 focus:border-accent-400 focus:outline-none rounded"
                  />
                  <span className="text-[10px] text-slate-400 font-mono">{s.id}</span>
                  <button onClick={() => moveSub(draft, i, j, -1)} disabled={j === 0} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Move up">
                    <ChevronUp size={11} />
                  </button>
                  <button onClick={() => moveSub(draft, i, j, 1)} disabled={j === c.subCategories.length - 1} className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30" title="Move down">
                    <ChevronDown size={11} />
                  </button>
                  <button onClick={() => deleteSub(draft, i, j)} className="p-1 text-rose-400 hover:text-rose-700" title="Delete sub-category">
                    <Trash2 size={11} />
                  </button>
                </div>
              ))}
              <button
                onClick={() => addSub(draft, i)}
                className="text-[11px] text-accent-600 hover:underline inline-flex items-center gap-1 ml-1"
              >
                <Plus size={11} /> Add sub-category
              </button>
            </div>
          </div>
        ))}

        <button
          onClick={() => addCategory(draft)}
          className="text-xs px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded inline-flex items-center gap-1"
        >
          <Plus size={12} /> Add category
        </button>
      </div>

      {error && (
        <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{error}</div>
      )}

      <div className="flex items-center justify-end gap-2 pt-1">
        {tree.updatedAt && (
          <span className="text-[10px] text-slate-400 mr-auto">
            Last updated {new Date(tree.updatedAt).toLocaleString()} by {tree.updatedBy}
          </span>
        )}
        <button
          onClick={() => setDraft(deepCloneCategories(tree.categories))}
          disabled={!dirty || saving}
          className="text-xs px-3 py-1.5 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 rounded"
        >
          Cancel
        </button>
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="text-xs px-3 py-1.5 bg-accent-500 hover:bg-accent-600 disabled:bg-slate-300 text-white rounded inline-flex items-center gap-1"
        >
          {saving ? <Loader2 size={11} className="animate-spin" /> : <Tags size={12} />} Save
        </button>
      </div>
    </div>
  );
}

function deepCloneCategories(cs: CategoryNode[]): CategoryNode[] {
  return cs.map(c => ({
    id: c.id, name: c.name, sortOrder: c.sortOrder,
    subCategories: c.subCategories.map(s => ({ id: s.id, name: s.name, sortOrder: s.sortOrder })),
  }));
}
function isCategoriesEqual(a: CategoryNode[], b: CategoryNode[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].id !== b[i].id || a[i].name !== b[i].name) return false;
    if (a[i].subCategories.length !== b[i].subCategories.length) return false;
    for (let j = 0; j < a[i].subCategories.length; j++) {
      if (a[i].subCategories[j].id !== b[i].subCategories[j].id || a[i].subCategories[j].name !== b[i].subCategories[j].name) return false;
    }
  }
  return true;
}
function nextSlug(prefix: string, existing: string[]): string {
  const used = new Set(existing);
  let n = 1;
  while (used.has(`${prefix}-${n}`)) n++;
  return `${prefix}-${n}`;
}

// ---------- Manage > Notifications: editable templates ----------
// One sub-tab per event (deck_submitted / deck_published / deck_rejected).
// Each shows email subject + email body + Teams body fields with a live
// preview pane below. The preview uses src/lib/templates.ts which mirrors
// the backend's renderTemplate, so what you see is what gets sent.
//
// Test send fires the rendered template at an admin-typed recipient via the
// existing OBO path (email = /me/sendMail with the admin's identity; Teams
// = direct chat with the admin as sender). PR 4 swaps the email leg to use
// a configurable service mailbox.

const EVENT_LABELS: Record<NotificationEvent, string> = {
  deck_submitted: 'Deck submitted',
  deck_published: 'Deck published',
  deck_rejected: 'Deck rejected',
};
const EVENTS: NotificationEvent[] = ['deck_submitted', 'deck_published', 'deck_rejected'];

function NotificationTemplatesTab({ showToast }: { showToast: (s: string) => void }) {
  const { data, refetch } = useApi<{ templates: NotificationTemplate[] }>(() => api.listNotificationTemplates());
  const [activeEvent, setActiveEvent] = useState<NotificationEvent>('deck_submitted');

  if (!data) {
    return <div className="text-sm text-slate-500 inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Loading templates…</div>;
  }
  const current = data.templates.find(t => t.event === activeEvent);
  if (!current) {
    return <div className="text-sm text-rose-600">Template for {activeEvent} not found.</div>;
  }
  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-600">
        Customize the email subject + body and Teams message body sent for each workflow event. Variables in <code className="font-mono">{'{{double-braces}}'}</code> are substituted at send time.
      </div>

      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {EVENTS.map(e => (
          <button
            key={e}
            onClick={() => setActiveEvent(e)}
            className={`px-3 py-1.5 text-xs font-medium border-b-2 ${
              activeEvent === e ? 'border-accent-500 text-accent-700' : 'border-transparent text-slate-600 hover:text-slate-800'
            }`}
          >
            {EVENT_LABELS[e]}
          </button>
        ))}
      </div>

      <NotificationTemplateEditor
        key={activeEvent}
        template={current}
        onSaved={() => refetch()}
        onReset={() => refetch()}
        showToast={showToast}
      />
    </div>
  );
}

const ALLOWED_VARS_LIST = '{{actorName}}, {{deckTitle}}, {{reason}}, {{actionUrl}}, {{appName}}';

function NotificationTemplateEditor({
  template, onSaved, onReset, showToast,
}: {
  template: NotificationTemplate;
  onSaved: () => void;
  onReset: () => void;
  showToast: (s: string) => void;
}) {
  const [emailSubject, setEmailSubject] = useState(template.emailSubject);
  const [emailBody, setEmailBody] = useState(template.emailBody);
  const [teamsBody, setTeamsBody] = useState(template.teamsBody);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testChannel, setTestChannel] = useState<'email' | 'teams'>('email');
  const [testing, setTesting] = useState(false);
  // Lock the preview to current saved state by default; recompute live on
  // each keystroke so admins see what gets sent without clicking anything.

  const dirty =
    emailSubject !== template.emailSubject ||
    emailBody !== template.emailBody ||
    teamsBody !== template.teamsBody;

  const previewSubject = renderTemplatePreview(emailSubject, SAMPLE_VARS);
  const previewEmail = renderTemplatePreview(emailBody, SAMPLE_VARS);
  const previewTeams = renderTemplatePreview(teamsBody, SAMPLE_VARS);

  async function save() {
    setSaving(true);
    try {
      await api.updateNotificationTemplate(template.event, { emailSubject, emailBody, teamsBody });
      showToast(`Saved ${EVENT_LABELS[template.event]} template`);
      onSaved();
    } catch (e: any) {
      const msg = e instanceof ApiError ? `${e.status} ${e.message}` : e?.message || 'Save failed';
      showToast(`Save failed: ${msg}`);
    } finally {
      setSaving(false);
    }
  }

  async function reset() {
    setResetting(true);
    try {
      const next = await api.resetNotificationTemplate(template.event);
      setEmailSubject(next.emailSubject);
      setEmailBody(next.emailBody);
      setTeamsBody(next.teamsBody);
      showToast(`Reset to defaults`);
      onReset();
    } catch (e: any) {
      showToast(`Reset failed: ${e?.message || 'unknown'}`);
    } finally {
      setResetting(false);
    }
  }

  async function sendTest() {
    if (!testTo.trim()) {
      showToast('Enter a recipient first');
      return;
    }
    setTesting(true);
    try {
      const r = await api.testNotificationTemplate(template.event, { to: testTo.trim(), channel: testChannel });
      if (r.sent) {
        showToast(`Test ${testChannel} sent${r.delivered ? ` (delivered via ${r.delivered})` : ''}`);
      } else {
        showToast(`Test not delivered${r.error ? `: ${r.error}` : ''}`);
      }
    } catch (e: any) {
      const msg = e instanceof ApiError ? `${e.status} ${e.message}` : e?.message || 'Test failed';
      showToast(`Test send failed: ${msg}`);
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="space-y-3">
      <div className="bg-white border border-slate-200 rounded p-3 space-y-2">
        <label className="block text-[11px] font-medium text-slate-700">Email subject</label>
        <input
          value={emailSubject}
          onChange={e => setEmailSubject(e.target.value)}
          maxLength={200}
          className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-accent-400"
        />
        <label className="block text-[11px] font-medium text-slate-700 mt-2">Email body (HTML)</label>
        <textarea
          value={emailBody}
          onChange={e => setEmailBody(e.target.value)}
          rows={6}
          className="w-full px-2 py-1.5 text-xs font-mono border border-slate-200 rounded focus:outline-none focus:border-accent-400"
        />
        <label className="block text-[11px] font-medium text-slate-700 mt-2">Teams message body (HTML)</label>
        <textarea
          value={teamsBody}
          onChange={e => setTeamsBody(e.target.value)}
          rows={5}
          className="w-full px-2 py-1.5 text-xs font-mono border border-slate-200 rounded focus:outline-none focus:border-accent-400"
        />
        <div className="text-[10px] text-slate-500">
          Variables: <span className="font-mono">{ALLOWED_VARS_LIST}</span>. Unknown braces are left as literal text so typos show up in preview.
        </div>
        <div className="flex items-center justify-end gap-2 pt-1">
          {template.updatedAt && (
            <span className="text-[10px] text-slate-400 mr-auto">
              Last updated {new Date(template.updatedAt).toLocaleString()} by {template.updatedBy}
            </span>
          )}
          <button
            onClick={reset}
            disabled={resetting || saving}
            className="text-xs px-3 py-1.5 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-slate-700 rounded inline-flex items-center gap-1"
          >
            {resetting ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Reset to defaults
          </button>
          <button
            onClick={save}
            disabled={!dirty || saving}
            className="text-xs px-3 py-1.5 bg-accent-500 hover:bg-accent-600 disabled:bg-slate-300 text-white rounded inline-flex items-center gap-1"
          >
            {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={12} />} Save
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded p-3 space-y-2">
        <div className="text-[11px] uppercase tracking-wide text-slate-500">Preview (sample values)</div>
        <div>
          <div className="text-[10px] text-slate-500 mb-1">Subject</div>
          <div className="text-sm text-slate-800 font-medium">{previewSubject}</div>
        </div>
        <div>
          <div className="text-[10px] text-slate-500 mb-1">Email body</div>
          <div
            className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded p-2 leading-snug"
            dangerouslySetInnerHTML={{ __html: previewEmail }}
          />
        </div>
        <div>
          <div className="text-[10px] text-slate-500 mb-1">Teams body</div>
          <div
            className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded p-2 leading-snug"
            dangerouslySetInnerHTML={{ __html: previewTeams }}
          />
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded p-3 space-y-2">
        <div className="text-[11px] uppercase tracking-wide text-slate-500">Send test</div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            value={testTo}
            onChange={e => setTestTo(e.target.value)}
            placeholder={testChannel === 'email' ? 'recipient@tenant.com' : 'recipient AAD object id (oid)'}
            className="flex-1 min-w-[200px] px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-accent-400"
          />
          <select
            value={testChannel}
            onChange={e => setTestChannel(e.target.value as 'email' | 'teams')}
            className="px-2 py-1.5 text-sm border border-slate-200 rounded"
          >
            <option value="email">Email</option>
            <option value="teams">Teams DM</option>
          </select>
          <button
            onClick={sendTest}
            disabled={testing || !testTo.trim()}
            className="text-xs px-3 py-1.5 bg-accent-500 hover:bg-accent-600 disabled:bg-slate-300 text-white rounded inline-flex items-center gap-1"
          >
            {testing ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} Send test
          </button>
        </div>
        <div className="text-[10px] text-slate-500">
          Uses the values shown above as sample data. Email sends from your mailbox; Teams sends from your account as a DM.
        </div>
      </div>
    </div>
  );
}

// ---------- Shared ----------

// ---------- Group restrictions picker ----------
// A deck.groupIds list controls who sees the deck in Browse once published:
//   - empty → every tenant member can see it
//   - non-empty → only members of one of those AAD groups can see it
//
// The picker lets a curator search AAD by name (via Graph OBO) and chip
// groups in/out. We pre-resolve already-saved groupIds to displayName via
// /groups/lookup so the chips render properly on open.

function GroupRestrictionsButton({
  deck,
  onUpdated,
  showToast,
  variant = 'pill',
}: {
  deck: Deck;
  onUpdated: (next: Deck) => void;
  showToast: (s: string) => void;
  variant?: 'pill' | 'inline';
}) {
  const [open, setOpen] = useState(false);
  const count = deck.groupIds.length;
  const label = count === 0 ? 'Anyone in tenant' : `${count} group${count === 1 ? '' : 's'}`;

  if (variant === 'inline') {
    return (
      <>
        <button
          onClick={() => setOpen(true)}
          className="text-[11px] px-2 py-1 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded inline-flex items-center gap-1"
          title="Restrict who can see this deck"
        >
          <Lock size={11} /> Access
        </button>
        {open && (
          <GroupPickerModal
            deck={deck}
            onClose={() => setOpen(false)}
            onUpdated={(next) => { setOpen(false); onUpdated(next); }}
            showToast={showToast}
          />
        )}
      </>
    );
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`text-[10px] px-1.5 py-0.5 rounded inline-flex items-center gap-1 ${
          count === 0
            ? 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            : 'bg-indigo-50 text-indigo-800 hover:bg-indigo-100 border border-indigo-200'
        }`}
        title="Restrict who can see this deck"
      >
        <Lock size={10} /> {label}
      </button>
      {open && (
        <GroupPickerModal
          deck={deck}
          onClose={() => setOpen(false)}
          onUpdated={(next) => { setOpen(false); onUpdated(next); }}
          showToast={showToast}
        />
      )}
    </>
  );
}

function GroupPickerModal({
  deck,
  onClose,
  onUpdated,
  showToast,
}: {
  deck: Deck;
  onClose: () => void;
  onUpdated: (next: Deck) => void;
  showToast: (s: string) => void;
}) {
  // Selected = the groups currently staged for save (mutates on chip toggle).
  // Cache = displayName lookup for every group we've ever seen, so toggling
  // a chip on after a search doesn't drop its name.
  const [selectedIds, setSelectedIds] = useState<string[]>(deck.groupIds);
  const [cache, setCache] = useState<Map<string, GraphGroup>>(new Map());
  const [resolving, setResolving] = useState(deck.groupIds.length > 0);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<GraphGroup[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  // Resolve pre-existing groupIds → displayName on open.
  useEffect(() => {
    if (deck.groupIds.length === 0) return;
    api.lookupGroups(deck.groupIds)
      .then(r => {
        setCache(prev => {
          const next = new Map(prev);
          for (const g of r.groups) next.set(g.id, g);
          return next;
        });
      })
      .catch(() => undefined)
      .finally(() => setResolving(false));
  }, [deck.groupIds]);

  // Debounced search. Empty query → Graph returns "my groups" (see backend).
  useEffect(() => {
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await api.searchGroups(query);
        if (cancelled) return;
        setSearchResults(r.groups);
        setCache(prev => {
          const next = new Map(prev);
          for (const g of r.groups) next.set(g.id, g);
          return next;
        });
      } catch (e: any) {
        if (!cancelled) {
          showToast(`Group search failed: ${e?.message || 'unknown'}`);
          setSearchResults([]);
        }
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, showToast]);

  function toggle(id: string) {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id],
    );
  }

  async function save() {
    setSaving(true);
    try {
      const next = await api.updateDeckGroups(deck.id, selectedIds);
      showToast(selectedIds.length === 0
        ? `Removed access restrictions on "${deck.title}"`
        : `Restricted "${deck.title}" to ${selectedIds.length} group${selectedIds.length === 1 ? '' : 's'}`);
      onUpdated(next);
    } catch (e: any) {
      const msg = e instanceof ApiError ? `${e.status} ${e.message}` : e?.message || 'Save failed';
      showToast(`Save failed: ${msg}`);
    } finally {
      setSaving(false);
    }
  }

  const dirty =
    selectedIds.length !== deck.groupIds.length ||
    selectedIds.some(id => !deck.groupIds.includes(id));

  return (
    <Modal title={`Restrict access — ${deck.title}`} onClose={saving ? () => {} : onClose}>
      <div className="space-y-3">
        <p className="text-[11px] text-slate-600">
          Pick AAD groups. Only members of one of these groups will see this deck in Browse.
          Leave empty for everyone in your tenant.
        </p>

        <div>
          <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
            Selected ({selectedIds.length})
          </div>
          {resolving ? (
            <div className="text-[11px] text-slate-500 inline-flex items-center gap-1">
              <Loader2 size={11} className="animate-spin" /> Resolving group names…
            </div>
          ) : selectedIds.length === 0 ? (
            <div className="text-[11px] text-slate-400 italic">No restrictions — anyone in the tenant can see this deck.</div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {selectedIds.map(id => {
                const g = cache.get(id);
                return (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 bg-indigo-50 text-indigo-800 border border-indigo-200 rounded"
                  >
                    {g?.displayName ?? id}
                    <button
                      onClick={() => toggle(id)}
                      className="text-indigo-500 hover:text-indigo-800"
                      title="Remove"
                    >
                      <X size={10} />
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search AAD groups by name…"
              className="w-full pl-7 pr-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-accent-400"
            />
          </div>
          <div className="mt-1 max-h-48 overflow-y-auto border border-slate-200 rounded bg-slate-50">
            {searching && !searchResults ? (
              <div className="text-[11px] text-slate-500 p-2 inline-flex items-center gap-1">
                <Loader2 size={11} className="animate-spin" /> Searching…
              </div>
            ) : !searchResults || searchResults.length === 0 ? (
              <div className="text-[11px] text-slate-400 italic p-2">
                {query ? 'No groups match.' : 'Your groups will appear here. Start typing to search the directory.'}
              </div>
            ) : (
              <ul className="divide-y divide-slate-200">
                {searchResults.map(g => {
                  const picked = selectedIds.includes(g.id);
                  return (
                    <li key={g.id}>
                      <button
                        onClick={() => toggle(g.id)}
                        className={`w-full text-left px-2 py-1.5 flex items-center gap-2 hover:bg-white ${picked ? 'bg-white' : ''}`}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center text-white text-[10px] ${
                          picked ? 'bg-accent-500 border-accent-500' : 'bg-white border-slate-300'
                        }`}>
                          {picked && <Check size={10} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-slate-800 truncate">{g.displayName}</div>
                          {g.description && (
                            <div className="text-[10px] text-slate-500 truncate">{g.description}</div>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button
            onClick={onClose}
            disabled={saving}
            className="text-xs px-3 py-1.5 bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-slate-700 rounded"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={!dirty || saving}
            className="text-xs px-3 py-1.5 bg-accent-500 hover:bg-accent-600 disabled:bg-slate-300 text-white rounded inline-flex items-center gap-1"
          >
            {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />} Save
          </button>
        </div>
      </div>
    </Modal>
  );
}

function LoukBanner({ message }: { message: string }) {
  return (
    <div className="bg-gradient-to-r from-accent-50 to-amber-50 border border-accent-200 rounded p-2.5 flex items-center gap-2.5 my-2 text-accent-900">
      <Loader2 size={14} className="animate-spin text-accent-500 flex-shrink-0" />
      <span className="text-xs font-medium">{message}…</span>
    </div>
  );
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded shadow-xl w-full max-w-sm">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200">
          <div className="text-sm font-medium text-slate-800">{title}</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={14} /></button>
        </div>
        <div className="p-3">{children}</div>
      </div>
    </div>
  );
}

function ConfirmDialog({
  title,
  message,
  confirmLabel = 'Confirm',
  danger = false,
  busy = false,
  onCancel,
  onConfirm,
}: {
  title: string;
  message: string;
  confirmLabel?: string;
  danger?: boolean;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal title={title} onClose={busy ? () => {} : onCancel}>
      <p className="text-sm text-slate-700 mb-4 whitespace-pre-line">{message}</p>
      <div className="flex justify-end gap-2">
        <button
          onClick={onCancel}
          disabled={busy}
          className="px-3 py-1.5 text-xs bg-slate-100 hover:bg-slate-200 disabled:opacity-50 text-slate-700 rounded"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={busy}
          className={`px-3 py-1.5 text-xs text-white rounded inline-flex items-center gap-1 ${
            danger
              ? 'bg-rose-600 hover:bg-rose-700 disabled:bg-rose-400'
              : 'bg-accent-500 hover:bg-accent-600 disabled:bg-accent-300'
          }`}
        >
          {busy ? 'Working…' : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map(p => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}
